import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEnhancedProfile } from "../hooks/useEnhancedProfile";
import profileApi from "../api/profileApi";
import subscriptionApi from "../api/subscriptionApi";
import { hotelBookingAPI, restaurantBookingAPI } from "../api/bookingApi";
import refundApi from "../api/refundApi";
import { API_CONFIG } from "../config/api.config";
import jwtTokenService from "../services/jwtTokenService";
import { toast } from "react-toastify";
import RefundRequestModal from "./RefundRequestModal";
import {
  User,
  Settings,
  CreditCard,
  Calendar,
  Star,
  Shield,
  Crown,
  Upload,
  Edit3,
  Edit2,
  Save,
  X,
  Loader2,
  AlertCircle,
  RefreshCw,
  Download,
  Trash2,
  AlertTriangle,
  Bell,
  BedDouble,
  Utensils,
  Lock,
  Globe,
  ArrowLeft,
  LogOut,
  Eye,
  EyeOff,
  Clock,
  Hotel,
  Search,
  MapPin,
  Users,
  Hash,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  XCircle,
} from "lucide-react";

const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");

  // Initialize user data from localStorage
  useEffect(() => {
    const jwtUserData = localStorage.getItem("trivana_user");
    if (jwtUserData) {
      const parsedUser = JSON.parse(jwtUserData);
      setUser(parsedUser);
    } else {
      const legacyUserData = localStorage.getItem("user");
      if (legacyUserData) {
        const parsedUser = JSON.parse(legacyUserData);
        setUser(parsedUser);
      }
    }
  }, []);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordUpdating, setPasswordUpdating] = useState(false);
  const [userBookings, setUserBookings] = useState({
    restaurantBookings: [],
    roomBookings: [],
    totalRestaurantBookings: 0,
    totalRoomBookings: 0,
  });
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [userSettings, setUserSettings] = useState({});
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [userPreferences, setUserPreferences] = useState({});
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [userSubscription, setUserSubscription] = useState({});
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [userPayments, setUserPayments] = useState({});
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [isExportingData, setIsExportingData] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [bookingSearchQuery, setBookingSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelBookingData, setCancelBookingData] = useState(null);

  // Refund Modal States
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundBooking, setRefundBooking] = useState(null);
  const [refundBookingType, setRefundBookingType] = useState(null);

  const getBookingReceiptDownloadUrl = (booking) => {
    const bookingType = (booking?.type || "").toString().toLowerCase();
    const bookingId = booking?.bookingId || booking?.id;

    if (!bookingId) {
      return null;
    }

    const endpointPrefix = bookingType.includes("restaurant")
      ? "restaurant-bookings"
      : "room-bookings";

    return `${API_CONFIG.BASE_URL}/${endpointPrefix}/${bookingId}/download-receipt`;
  };

  const getBookingReceiptUrlEndpoint = (booking) => {
    const bookingType = (booking?.type || "").toString().toLowerCase();
    const bookingId = booking?.bookingId || booking?.id;

    if (!bookingId) {
      return null;
    }

    const endpointPrefix = bookingType.includes("restaurant")
      ? "restaurant-bookings"
      : "room-bookings";

    return `${API_CONFIG.BASE_URL}/${endpointPrefix}/${bookingId}/receipt-url`;
  };

  const handleDownloadReceipt = async (booking) => {
    try {
      const receiptUrlEndpoint = getBookingReceiptUrlEndpoint(booking);
      if (!receiptUrlEndpoint) {
        toast.error("Receipt URL not available for this booking");
        return;
      }

      const response = await fetch(receiptUrlEndpoint);
      if (response.ok) {
        const data = await response.json();
        if (data?.available && data?.pdfUrl) {
          const baseHost = API_CONFIG.BASE_URL.replace(/\/api$/, "");
          window.open(`${baseHost}${data.pdfUrl}`, "_blank", "noopener,noreferrer");
          return;
        }
      }

      const downloadUrl = getBookingReceiptDownloadUrl(booking);
      if (!downloadUrl) {
        toast.error("Receipt URL not available for this booking");
        return;
      }

      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Error opening receipt:", error);
      toast.error("Unable to download receipt right now. Please try again.");
    }
  };

  const {
    profile,
    loading,
    error,
    updating,
    updateBasicProfile,
    updatePreferences,
    updateSettings,
    uploadProfilePicture,
    refreshProfile,
  } = useEnhancedProfile(user?.user_id || user?.id);

  // Extract user ID
  const userId = user?.user_id || user?.id || user?.userId;

  // Handle logout
  const handleLogout = () => {
    jwtTokenService.logout();
    toast.success("Logged out successfully!");
    navigate("/");
    window.location.reload();
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    try {
      await profileApi.deleteAccount(userId);
      toast.success("Account deleted successfully");
      handleLogout();
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Failed to delete account");
    }
    setShowDeleteAccountModal(false);
  };

  // Fetch user bookings
  const fetchUserBookings = async () => {
    if (!userId) return;

    setLoadingBookings(true);
    try {
      const bookings = await profileApi.getAllUserBookings(userId);
      setUserBookings(bookings);
    } catch (error) {
      console.error("Error fetching user bookings:", error);
    } finally {
      setLoadingBookings(false);
    }
  };

  // Handle booking cancellation with refund modal
  const handleCancelBooking = async (booking, bookingType) => {
    try {
      setRefundBooking(booking);
      setRefundBookingType(bookingType);
      setShowRefundModal(true);
    } catch (error) {
      console.error("Cancel booking error:", error);
      toast.error(
        "❌ Failed to prepare cancellation: " +
        (error.message || "Unknown error"),
      );
    }
  };

  // Handle refund created successfully
  const handleRefundCreated = async (refundData) => {
    try {
      toast.success(
        "✅ Refund request submitted successfully! You will receive email updates.",
      );

      // Refresh bookings after a short delay to ensure backend has processed it
      setTimeout(async () => {
        try {
          await fetchUserBookings();
        } catch (error) {
          console.error("Error refreshing bookings after refund:", error);
          // Don't show error - user already got success message
        }
      }, 1000);
    } catch (error) {
      console.error("Error in refund success handler:", error);
    }
  };

  // Confirm and execute cancellation
  const confirmCancellation = async () => {
    if (!cancelBookingData) return;

    try {
      setShowCancelModal(false);
      setLoadingBookings(true);

      const { booking, bookingType } = cancelBookingData;

      // Call appropriate API
      if (bookingType === "hotel") {
        await hotelBookingAPI.cancelBooking(booking.bookingId);
      } else {
        await restaurantBookingAPI.cancelBooking(booking.id);
      }

      toast.success(
        "🎉 Booking cancelled successfully! Check your email for confirmation.",
      );

      // Refresh bookings list
      await fetchUserBookings();
    } catch (error) {
      console.error("Cancel booking error:", error);
      toast.error(
        "❌ Failed to cancel booking: " + (error.message || "Unknown error"),
      );
    } finally {
      setLoadingBookings(false);
      setCancelBookingData(null);
    }
  };

  // Fetch user settings
  const fetchUserSettings = async () => {
    if (!userId) return;

    setLoadingSettings(true);
    try {
      const settings = await profileApi.getUserSettings(userId);
      setUserSettings(settings);
    } catch (error) {
      console.error("Error fetching user settings:", error);
      // Set default settings if fetch fails
      setUserSettings({
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true,
        profileVisibility: "PUBLIC",
        dataSharing: false,
        marketingEmails: false,
        autoFillBookingInfo: true,
        savedPaymentMethods: true,
        quickBooking: false,
        preferredLanguage: "EN",
        currency: "USD",
        timeZone: "UTC",
        twoFactorAuth: false,
        loginAlerts: true,
        accountLockout: false,
      });
    } finally {
      setLoadingSettings(false);
    }
  };

  // Update preference locally (will save on Save button click)
  const handlePreferenceChange = (field, value) => {
    const updatedPreferences = { ...userPreferences, [field]: value };
    setUserPreferences(updatedPreferences);

    // Also update formData for saving
    setFormData({ ...formData, [field]: value });
  };

  // Auto-save setting when changed
  const handleSettingChange = async (field, value) => {
    try {
      const updatedSettings = { ...userSettings, [field]: value };
      setUserSettings(updatedSettings);
      setFormData({ ...formData, [field]: value });

      // Auto-save to backend
      const userId = user?.user_id || user?.id;
      await profileApi.updateSettings(userId, { [field]: value });
    } catch (error) {
      console.error("Error auto-saving setting:", error);
      // Revert the change if save failed
      setUserSettings((prev) => ({ ...prev, [field]: userSettings[field] }));
      setFormData((prev) => ({ ...prev, [field]: userSettings[field] }));
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setPasswordUpdating(true);
    try {
      const userId = user?.user_id || user?.id;
      await profileApi.changePassword(userId, passwordData);

      toast.success("Password changed successfully!");
      setShowChangePasswordModal(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error(error.response?.data?.message || "Failed to change password");
    } finally {
      setPasswordUpdating(false);
    }
  };

  // Handle data export as PDF
  const handleExportData = async () => {
    setIsExportingData(true);
    try {
      const userId = user?.user_id || user?.id;
      // Force fresh data fetch with timestamp to bypass cache
      const response = await profileApi.exportUserData(userId, {
        timestamp: Date.now(),
      });

      // Dynamically import jsPDF
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF();

      // PDF Styling
      const primaryColor = [238, 46, 36]; // #EE2E24
      const secondaryColor = [255, 196, 0]; // #FFC400
      const textColor = [46, 46, 46]; // #2E2E2E
      const lightGray = [200, 200, 200];

      let yPos = 20;
      const lineHeight = 6;
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const contentWidth = pageWidth - margin * 2;

      // Header with gradient effect
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 40, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text("TRIVANA HOSPITALITY", pageWidth / 2, 20, { align: "center" });

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text("User Data Export Report", pageWidth / 2, 30, {
        align: "center",
      });

      yPos = 50;

      // Export Information
      doc.setTextColor(...textColor);
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.text(
        `Export Date: ${new Date().toLocaleString("en-IN")}`,
        margin,
        yPos,
      );
      yPos += lineHeight;
      doc.text(`User ID: ${userId}`, margin, yPos);
      yPos += lineHeight * 2;

      // Helper function to check if new page is needed
      const checkPageBreak = (requiredSpace) => {
        if (yPos + requiredSpace > pageHeight - 30) {
          doc.addPage();
          yPos = 20;
          return true;
        }
        return false;
      };

      // Helper function to add section header
      const addSectionHeader = (title) => {
        checkPageBreak(15);

        doc.setFillColor(...secondaryColor);
        doc.rect(margin, yPos - 4, contentWidth, 9, "F");
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text(title, margin + 3, yPos + 2);
        yPos += 12;
      };

      // Helper function to add field
      const addField = (label, value) => {
        checkPageBreak(10);

        doc.setTextColor(...textColor);
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text(`${label}:`, margin + 3, yPos);

        doc.setFont("helvetica", "normal");
        let displayValue = "N/A";

        if (value === null || value === undefined || value === "") {
          displayValue = "N/A";
        } else if (typeof value === "boolean") {
          displayValue = value ? "Yes" : "No";
        } else if (typeof value === "object") {
          displayValue = JSON.stringify(value);
        } else {
          displayValue = String(value);
        }

        // Split long text
        const maxWidth = contentWidth - 65;
        const splitText = doc.splitTextToSize(displayValue, maxWidth);
        doc.text(splitText, margin + 62, yPos);

        yPos += splitText.length * lineHeight;
      };

      // USER INFORMATION
      if (response.userInfo || response.user) {
        const userInfo = response.userInfo || response.user || {};
        addSectionHeader("USER INFORMATION");

        addField("User ID", userInfo.userId || userInfo.user_id || userId);
        addField("Full Name", userInfo.fullName || userInfo.name || "N/A");
        addField("Email", userInfo.email || "N/A");
        addField("Phone", userInfo.phone || userInfo.mobile || "N/A");
        addField(
          "Account Created",
          userInfo.createdAt || userInfo.created_at || "N/A",
        );

        yPos += 5;
      }

      // PROFILE DETAILS
      if (response.profile) {
        addSectionHeader("PROFILE DETAILS");

        const profile = response.profile;
        addField("Address", profile.address || "Not provided");
        addField("City", profile.city || "Not provided");
        addField("State", profile.state || "Not provided");
        addField("Country", profile.country || "Not provided");
        addField(
          "PIN Code",
          profile.pinCode || profile.zipCode || "Not provided",
        );
        addField("Gender", profile.gender || "Not specified");
        addField("Date of Birth", profile.dateOfBirth || "Not provided");
        addField("Nationality", profile.nationality || "Not provided");
        addField("Marital Status", profile.maritalStatus || "Not provided");
        addField("Occupation", profile.occupation || "Not provided");
        addField(
          "Emergency Contact",
          profile.emergencyContact || "Not provided",
        );

        yPos += 5;
      }

      // MEMBERSHIP & SUBSCRIPTION
      if (response.subscription) {
        addSectionHeader("MEMBERSHIP & SUBSCRIPTION");

        const sub = response.subscription;
        addField(
          "Membership Tier",
          sub.membershipTier || sub.planType || sub.tier || "BASIC",
        );
        addField("Status", sub.isActive ? "Active" : "Inactive");
        addField("Member Since", sub.memberSince || "N/A");
        addField(
          "Plan Start Date",
          sub.planStartDate || sub.startDate || sub.memberSince || "N/A",
        );
        addField("Plan End Date", sub.planEndDate || sub.endDate || "N/A");
        addField(
          "Points Balance",
          sub.pointsBalance || sub.loyaltyPoints || sub.rewardPoints || 0,
        );
        addField("Lifetime Points", sub.lifetimePoints || sub.totalPoints || 0);

        yPos += 5;
      }

      // PREFERENCES
      if (response.preferences) {
        addSectionHeader("USER PREFERENCES");

        const pref = response.preferences;
        addField("Preferred Room Type", pref.preferredRoomType || "Standard");
        addField("AC Preference", pref.acPreference || "AC");
        addField("Bed Preference", pref.bedPreference || "Any");
        addField("View Preference", pref.viewPreference || "Any");
        addField("Food Preference", pref.foodPreference || "Any");
        addField("Dietary Restrictions", pref.dietaryRestrictions || "None");
        addField("Cuisine Preference", pref.cuisinePreference || "Any");
        addField("Special Requests", pref.specialRequests || "None");

        yPos += 5;
      }

      // BOOKING HISTORY
      if (response.bookings && response.bookings.length > 0) {
        addSectionHeader("BOOKING HISTORY");

        doc.setFontSize(8);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(...textColor);
        doc.text(
          `Total Bookings: ${response.bookings.length}`,
          margin + 3,
          yPos,
        );
        yPos += lineHeight * 2;

        response.bookings.forEach((booking, index) => {
          checkPageBreak(35);

          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.text(`Booking #${index + 1}`, margin + 5, yPos);
          yPos += lineHeight;

          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);

          // Booking type and location
          addField("  Type", booking.bookingType || "N/A");
          if (booking.hotelName) {
            addField("  Hotel", booking.hotelName);
            if (booking.roomType) {
              addField("  Room Type", booking.roomType);
            }
          }
          if (booking.restaurantName) {
            addField("  Restaurant", booking.restaurantName);
            if (booking.tableNumber) {
              addField("  Table", booking.tableNumber);
            }
          }

          // Dates
          if (booking.checkIn && booking.checkOut) {
            addField("  Check In", booking.checkIn);
            addField("  Check Out", booking.checkOut);
          } else if (booking.bookingDate) {
            addField("  Booking Date", booking.bookingDate);
            if (booking.bookingTime) {
              addField("  Booking Time", booking.bookingTime);
            }
          }

          // Status and amount
          addField("  Status", booking.status || "N/A");
          const amount = booking.totalAmount || booking.amount || 0;
          addField("  Amount", "Rs " + amount);

          yPos += 3;
        });

        yPos += 5;
      }

      // PAYMENT METHODS
      if (response.paymentMethods && response.paymentMethods.length > 0) {
        addSectionHeader("SAVED PAYMENT METHODS");

        doc.setFontSize(8);
        doc.setFont("helvetica", "italic");
        doc.text(
          `Total Payment Methods: ${response.paymentMethods.length}`,
          margin + 3,
          yPos,
        );
        yPos += lineHeight * 2;

        response.paymentMethods.forEach((pm, index) => {
          checkPageBreak(20);

          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.text(`Payment Method #${index + 1}`, margin + 5, yPos);
          yPos += lineHeight;

          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);

          addField("  Type", pm.methodType || "N/A");
          addField("  Last Used", pm.lastUsed || "Never");

          yPos += 3;
        });
      }

      // Add summary if no data
      if (
        !response.userInfo &&
        !response.profile &&
        !response.subscription &&
        !response.bookings &&
        !response.paymentMethods
      ) {
        checkPageBreak(30);

        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(200, 0, 0);
        doc.text("No data available for export", pageWidth / 2, yPos, {
          align: "center",
        });
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...textColor);
        doc.text(
          "Please ensure your profile is complete and try again.",
          pageWidth / 2,
          yPos,
          { align: "center" },
        );
      }

      // Footer on all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(...lightGray);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
        doc.text(
          "© Trivana Hospitality - Confidential User Data",
          pageWidth / 2,
          pageHeight - 5,
          { align: "center" },
        );
      }

      // Save PDF
      const fileName = `Trivana-UserData-${userId}-${new Date().toISOString().split("T")[0]
        }.pdf`;
      doc.save(fileName);

      toast.success("✅ Your data has been exported as PDF successfully!");
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("Failed to export data. Please try again.");
    } finally {
      setIsExportingData(false);
    }
  };

  // Fetch user preferences
  const fetchUserPreferences = async () => {
    if (!userId) return;

    setLoadingPreferences(true);
    try {
      const preferences = await profileApi.getPreferences(userId);
      setUserPreferences(preferences);
    } catch (error) {
      console.error("Error fetching user preferences:", error);
      // Set default preferences if fetch fails
      setUserPreferences({
        preferredRoomType: "Standard",
        acPreference: "AC",
        bedPreference: "Queen",
        viewPreference: "Any",
        floorPreference: "Any",
        foodPreference: "Veg",
        dietaryRestrictions: "None",
        cuisinePreference: "Indian",
        mealPlan: "Breakfast Included",
        specialRequests: "",
        accessibilityNeeds: "None",
        loyaltyProgramEnrollment: false,
      });
    } finally {
      setLoadingPreferences(false);
    }
  };

  // Fetch user subscription
  const fetchUserSubscription = async () => {
    if (!userId) return;

    setLoadingSubscription(true);
    try {
      // Use subscription API to get current plan
      const currentPlanResponse = await subscriptionApi.getCurrentPlan(userId);

      if (currentPlanResponse.success) {
        const planData = currentPlanResponse.data;

        setUserSubscription({
          isActive: planData.membershipTier !== "BASIC",
          planName: planData.membershipTier || "Basic",
          planType: planData.membershipTier || "BASIC",
          memberSince: planData.memberSince,
          startDate: planData.startDate,
          endDate: planData.endDate,
          totalBookings: planData.totalBookings || 0,
          loyaltyPoints: planData.loyaltyPoints || 0,
          rewardPoints: planData.rewardPoints || 0,
          activeCoupons: planData.activeCoupons || 0,
          features:
            planData.membershipTier === "BASIC"
              ? []
              : ["Premium features available"],
        });
      } else {
        throw new Error(currentPlanResponse.error);
      }
    } catch (error) {
      console.error("Error fetching user subscription:", error);
      // Set default subscription if fetch fails
      setUserSubscription({
        isActive: false,
        planName: "Basic",
        planType: "BASIC",
        startDate: null,
        endDate: null,
        totalBookings: 0,
        memberSince: null,
        loyaltyPoints: 0,
        rewardPoints: 0,
        activeCoupons: 0,
      });
    } finally {
      setLoadingSubscription(false);
    }
  };

  // Fetch user payments
  const fetchUserPayments = async () => {
    if (!userId) return;

    setLoadingPayments(true);
    try {
      const payments = await profileApi.getPaymentDetails(userId);
      setUserPayments(payments);
    } catch (error) {
      console.error("Error fetching user payments:", error);
      // Set default payments if fetch fails
      setUserPayments({
        totalSpent: 0,
        preferredMethod: "Not set",
        lastUsedMethod: "None",
        savedMethods: [],
      });
    } finally {
      setLoadingPayments(false);
    }
  };

  // Fetch bookings when tab changes to bookings
  useEffect(() => {
    if (activeTab === "bookings") {
      fetchUserBookings();
    }
  }, [activeTab, userId]);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [bookingSearchQuery]);

  // Fetch settings when tab changes to settings
  useEffect(() => {
    if (activeTab === "settings") {
      fetchUserSettings();
    }
  }, [activeTab, userId]);

  // Fetch preferences when tab changes to preferences
  useEffect(() => {
    if (activeTab === "preferences") {
      fetchUserPreferences();
    }
  }, [activeTab, userId]);

  // Fetch subscription when tab changes to subscription
  useEffect(() => {
    if (activeTab === "subscription") {
      fetchUserSubscription();
    }
  }, [activeTab, userId]);

  // Fetch payments when tab changes to payments
  useEffect(() => {
    if (activeTab === "payments") {
      fetchUserPayments();
    }
  }, [activeTab, userId]);

  // Fetch subscription on component mount for basic tab display
  useEffect(() => {
    if (userId) {
      fetchUserSubscription();
    }
  }, [userId]);

  // Refresh subscription when returning from membership page
  useEffect(() => {
    if (location.state?.refreshSubscription && userId) {
      fetchUserSubscription();
      // Clear the state to prevent repeated refreshes
      window.history.replaceState({}, document.title);
    }
  }, [location.state, userId]);

  // Auto-refresh bookings every 30 seconds when on bookings tab
  useEffect(() => {
    let interval;
    if (activeTab === "bookings") {
      interval = setInterval(fetchUserBookings, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab, userId]);

  const tabs = [
    { id: "basic", label: "Basic Info", icon: User },
    { id: "bookings", label: "Bookings", icon: Calendar },
    { id: "payments", label: "Payments", icon: CreditCard },
    { id: "preferences", label: "Preferences", icon: Settings },
    { id: "subscription", label: "Membership", icon: Star },
    { id: "settings", label: "Settings", icon: Shield },
  ];

  const handleEditToggle = () => {
    if (editMode) {
      // Exiting edit mode - clear form data
      setFormData({});
    } else {
      // Entering edit mode - initialize form data with current profile data
      const basic = profile?.basic || {};
      setFormData({
        fullName: basic.fullName || user?.name || "",
        email: basic.email || user?.email || "",
        phone: basic.phone || user?.phone || "",
        gender: basic.gender || "",
        dateOfBirth: basic.dateOfBirth || "",
        nationality: basic.nationality || "",
        maritalStatus: basic.maritalStatus || "",
        occupation: basic.occupation || "",
        alternatePhone: basic.alternatePhone || "",
        address: basic.address || "",
        city: basic.city || "",
        state: basic.state || "",
        pincode: basic.pincode || "",
        country: basic.country || "",
        emergencyContactName: basic.emergencyContactName || "",
        emergencyContactPhone: basic.emergencyContactPhone || "",
      });
    }
    setEditMode(!editMode);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const userId = user?.user_id || user?.id;

      switch (activeTab) {
        case "basic":
          const updatedProfile = await profileApi.updateBasicProfile(
            userId,
            formData,
          );

          // Update the profile state with new data
          if (updatedProfile) {
            // Update localStorage with ALL updated user data
            const currentUser = localStorage.getItem("trivana_user");
            if (currentUser) {
              const parsedUser = JSON.parse(currentUser);
              const updatedUser = {
                ...parsedUser,
                // Basic Information
                name: updatedProfile.fullName || parsedUser.name,
                email: updatedProfile.email || parsedUser.email,
                phone: updatedProfile.phone || parsedUser.phone,
                // Additional Profile Fields
                gender: updatedProfile.gender || parsedUser.gender,
                dateOfBirth:
                  updatedProfile.dateOfBirth || parsedUser.dateOfBirth,
                nationality:
                  updatedProfile.nationality || parsedUser.nationality,
                maritalStatus:
                  updatedProfile.maritalStatus || parsedUser.maritalStatus,
                occupation: updatedProfile.occupation || parsedUser.occupation,
                // Contact Information
                alternatePhone:
                  updatedProfile.alternatePhone || parsedUser.alternatePhone,
                // Address Information
                address: updatedProfile.address || parsedUser.address,
                city: updatedProfile.city || parsedUser.city,
                state: updatedProfile.state || parsedUser.state,
                country: updatedProfile.country || parsedUser.country,
                pincode: updatedProfile.pincode || parsedUser.pincode,
                // Emergency Contact
                emergencyContactName:
                  updatedProfile.emergencyContactName ||
                  parsedUser.emergencyContactName,
                emergencyContactPhone:
                  updatedProfile.emergencyContactPhone ||
                  parsedUser.emergencyContactPhone,
                // Profile Picture
                profilePicture:
                  updatedProfile.profilePicture || parsedUser.profilePicture,
              };
              localStorage.setItem("trivana_user", JSON.stringify(updatedUser));
              setUser(updatedUser);
            }

            // Refresh the profile data from the server
            await refreshProfile();

            // Dispatch custom event to notify navbar and other components
            window.dispatchEvent(
              new CustomEvent("profileUpdated", {
                detail: { user: updatedProfile },
              }),
            );
          }
          break;
        case "preferences":
          await profileApi.updatePreferences(userId, formData);
          await refreshProfile();
          break;
        case "settings":
          await profileApi.updateSettings(userId, formData);
          await refreshProfile();
          break;
      }
      setEditMode(false);
      setFormData({});
      toast.success("Profile updated successfully!");
    } catch (err) {
      console.error("Save failed:", err);
      toast.error("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Define max file size (same as backend: 5MB)
      const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
      const MAX_FILE_SIZE_MB = 5;

      // Validate file type - support all image formats
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/bmp",
        "image/tiff",
        "image/x-tiff",
        "image/svg+xml",
        "image/x-icon",
        "image/vnd.microsoft.icon",
      ];

      if (
        !allowedTypes.includes(file.type) &&
        !file.type.startsWith("image/")
      ) {
        toast.error(
          "❌ Unsupported file type. Please upload an image (JPG, PNG, WebP, GIF, BMP, etc.)",
        );
        return;
      }

      // Validate file size (5MB max)
      if (file.size > MAX_FILE_SIZE) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        toast.error(
          `❌ File size is too large!\n\nCurrent size: ${fileSizeMB} MB\nMaximum allowed: ${MAX_FILE_SIZE_MB} MB\n\nPlease select a smaller image file.`,
        );
        return;
      }

      try {
        const userId = user?.user_id || user?.id;
        const response = await profileApi.uploadProfilePicture(userId, file);

        if (response.profilePicture) {
          // Success message
          toast.success("✅ Profile picture uploaded successfully!");

          // Refresh the profile data to show new picture
          window.location.reload(); // Simple refresh for now
        }
      } catch (err) {
        console.error("Upload failed:", err);

        // Show backend error message if available
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to upload profile picture";

        if (errorMessage.includes("size") || errorMessage.includes("Size")) {
          toast.error(
            `❌ ${errorMessage}\n\nPlease upload an image smaller than 5MB.`,
          );
        } else if (
          errorMessage.includes("type") ||
          errorMessage.includes("format")
        ) {
          toast.error(`❌ ${errorMessage}`);
        } else {
          toast.error(`❌ ${errorMessage}`);
        }
      }
    }
  };

  // Show loading if user not loaded yet
  if (!user) {
    return (
      <div
        style={{
          paddingTop: "90px",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #FFF5F5 0%, #FFF9E6 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Loader2
            size={48}
            style={{
              animation: "spin 1s linear infinite",
              color: "#EE2E24",
              marginBottom: "20px",
            }}
          />
          <p style={{ fontSize: "16px", color: "#4a5568" }}>
            Loading user data...
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          paddingTop: "90px",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #FFF5F5 0%, #FFF9E6 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Loader2
            size={48}
            style={{
              animation: "spin 1s linear infinite",
              color: "#EE2E24",
              marginBottom: "20px",
            }}
          />
          <p style={{ fontSize: "16px", color: "#4a5568" }}>
            Loading your profile...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          paddingTop: "90px",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #FFF5F5 0%, #FFF9E6 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "40px",
            maxWidth: "500px",
            boxShadow: "0 20px 40px rgba(238, 46, 36, 0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              color: "#EE2E24",
              marginBottom: "20px",
            }}
          >
            <AlertCircle size={24} />
            <h3 style={{ margin: 0, fontSize: "20px", fontWeight: "700" }}>
              Error Loading Profile
            </h3>
          </div>
          <p style={{ color: "#4a5568", marginBottom: "30px" }}>{error}</p>
          <button
            onClick={() => navigate(-1)}
            style={{
              width: "100%",
              padding: "12px",
              background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              fontSize: "16px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const renderBasicInfo = () => {
    const basic = profile?.basic || {};

    if (editMode) {
      return (
        <div className="space-y-4">
          {/* Personal Information Section */}
          <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-4 shadow-md">
            <h4 className="text-base font-bold text-red-700 mb-3">
              Personal Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-red-600 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.fullName || basic.fullName || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, fullName: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-red-200/60 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-red-600 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email || basic.email || ""}
                  disabled
                  className="w-full px-3 py-2 border border-red-200/60 rounded-lg bg-gray-100 cursor-not-allowed text-sm text-gray-600"
                  title="Email cannot be changed for security reasons"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email cannot be changed for security reasons
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-red-600 mb-1">
                  Gender
                </label>
                <select
                  value={formData.gender || basic.gender || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, gender: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-red-200/60 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-red-600 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={
                    formData.dateOfBirth ||
                    (basic.dateOfBirth
                      ? new Date(basic.dateOfBirth).toISOString().split("T")[0]
                      : "")
                  }
                  onChange={(e) =>
                    setFormData({ ...formData, dateOfBirth: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-red-200/60 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-red-600 mb-1">
                  Nationality
                </label>
                <input
                  type="text"
                  value={formData.nationality || basic.nationality || "Indian"}
                  onChange={(e) =>
                    setFormData({ ...formData, nationality: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-red-200/60 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-red-600 mb-1">
                  Marital Status
                </label>
                <select
                  value={formData.maritalStatus || basic.maritalStatus || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, maritalStatus: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-red-200/60 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400"
                >
                  <option value="">Select Status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-red-600 mb-1">
                  Occupation
                </label>
                <input
                  type="text"
                  value={formData.occupation || basic.occupation || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, occupation: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-red-200/60 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400"
                  placeholder="Your occupation"
                />
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-4 shadow-md">
            <h4 className="text-base font-bold text-red-700 mb-3">
              Contact Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-red-600 mb-1">
                  Primary Phone *
                </label>
                <input
                  type="tel"
                  value={formData.phone || basic.phone || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-red-200/60 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-red-600 mb-1">
                  Alternate Phone
                </label>
                <input
                  type="tel"
                  value={formData.alternatePhone || basic.alternatePhone || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, alternatePhone: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-red-200/60 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400"
                />
              </div>
            </div>
          </div>

          {/* Address Information Section */}
          <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-4 shadow-md">
            <h4 className="text-base font-bold text-red-700 mb-3">
              Address Information
            </h4>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs font-semibold text-red-600 mb-1">
                  Full Address
                </label>
                <textarea
                  value={formData.address || basic.address || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-red-200/60 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400"
                  rows="2"
                  placeholder="Enter your complete address"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city || basic.city || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.state || basic.state || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, state: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pincode
                  </label>
                  <input
                    type="text"
                    value={formData.pincode || basic.pincode || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, pincode: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                    pattern="[0-9]{6}"
                    maxLength="6"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  value={formData.country || basic.country || "India"}
                  onChange={(e) =>
                    setFormData({ ...formData, country: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact Section */}
          <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-3">
              Emergency Contact
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Name
                </label>
                <input
                  type="text"
                  value={
                    formData.emergencyContactName ||
                    basic.emergencyContactName ||
                    ""
                  }
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emergencyContactName: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Contact person name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Phone
                </label>
                <input
                  type="tel"
                  value={
                    formData.emergencyContactPhone ||
                    basic.emergencyContactPhone ||
                    ""
                  }
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emergencyContactPhone: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Emergency contact number"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-t border-red-200/60">
            <button
              onClick={handleEditToggle}
              className="px-6 py-2 border border-red-300 text-red-600 font-semibold rounded-lg hover:bg-red-50 transition-all duration-200 flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className="space-y-4"
        style={{
          gap: window.innerWidth < 768 ? "12px" : "16px",
        }}
      >
        {/* Profile Header - Compact */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-red-500 to-red-600 px-4 sm:px-6 py-4">
            <div
              className="flex items-center justify-between"
              style={{
                flexDirection: window.innerWidth < 640 ? "column" : "row",
                gap: window.innerWidth < 640 ? "12px" : "0",
              }}
            >
              <div className="flex items-center gap-4">
                <div className="relative">
                  <label className="cursor-pointer">
                    <img
                      src={
                        basic.profilePicture ||
                        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
                      }
                      alt={basic.fullName}
                      className="rounded-full object-cover ring-2 ring-white/50 hover:ring-white/70 transition-all"
                      style={{
                        width: window.innerWidth < 640 ? "56px" : "64px",
                        height: window.innerWidth < 640 ? "56px" : "64px",
                      }}
                    />
                    <div className="absolute bottom-0 right-0 bg-white text-red-600 p-1 rounded-full hover:bg-gray-50">
                      <Upload className="w-3 h-3" />
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      accept="image/*"
                    />
                  </label>
                </div>
                <div>
                  <h3
                    className="font-bold text-white"
                    style={{
                      fontSize: window.innerWidth < 640 ? "18px" : "20px",
                    }}
                  >
                    {basic.fullName}
                  </h3>
                  <p
                    className="text-white/90"
                    style={{
                      fontSize: window.innerWidth < 640 ? "12px" : "14px",
                    }}
                  >
                    {basic.email}
                  </p>
                </div>
              </div>
              <button
                onClick={handleEditToggle}
                className="px-4 py-2 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-50 flex items-center gap-2"
                style={{
                  fontSize: window.innerWidth < 640 ? "12px" : "14px",
                  padding: window.innerWidth < 640 ? "6px 12px" : "8px 16px",
                }}
              >
                <Edit2 className="w-4 h-4" />
                {window.innerWidth < 640 ? "Edit" : "Edit Profile"}
              </button>
            </div>
          </div>

          <div className="px-4 sm:px-6 py-3 bg-gray-50 border-t border-gray-200">
            <div
              className="flex items-center gap-3 sm:gap-6 text-gray-600"
              style={{
                fontSize: window.innerWidth < 640 ? "11px" : "14px",
                flexWrap: "wrap",
              }}
            >
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Joined{" "}
                {new Date(basic.createdAt).toLocaleDateString("en-IN", {
                  month: "short",
                  year: "numeric",
                })}
              </span>
              <span className="flex items-center gap-1">
                <Star className="w-4 h-4 text-yellow-500" />
                {userSubscription.loyaltyPoints || 0} Points
              </span>
              <span className="flex items-center gap-1">
                <Shield className="w-4 h-4 text-green-600" />
                {userSubscription.planName || "Basic"} Member
              </span>
            </div>
          </div>
        </div>

        {/* Personal Information - Compact Grid */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div
            className="border-b border-gray-200"
            style={{
              padding: window.innerWidth < 768 ? "10px 12px" : "12px 24px",
            }}
          >
            <h4
              className="font-semibold text-gray-800 uppercase tracking-wide"
              style={{
                fontSize: window.innerWidth < 768 ? "11px" : "14px",
              }}
            >
              Personal Information
            </h4>
          </div>
          <div
            style={{
              padding: window.innerWidth < 768 ? "12px" : "16px 24px",
            }}
          >
            <div
              className="grid"
              style={{
                gridTemplateColumns:
                  window.innerWidth < 768
                    ? "repeat(2, 1fr)"
                    : window.innerWidth < 1024
                      ? "repeat(2, 1fr)"
                      : "repeat(3, 1fr)",
                gap: window.innerWidth < 768 ? "12px 8px" : "16px 24px",
              }}
            >
              <div>
                <label
                  className="block font-medium text-gray-500"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Gender
                </label>
                <p
                  className="text-gray-900 font-medium"
                  style={{
                    fontSize: window.innerWidth < 768 ? "12px" : "14px",
                  }}
                >
                  {basic.gender || "Not specified"}
                </p>
              </div>
              <div>
                <label
                  className="block font-medium text-gray-500"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Date of Birth
                </label>
                <p
                  className="text-gray-900 font-medium"
                  style={{
                    fontSize: window.innerWidth < 768 ? "12px" : "14px",
                  }}
                >
                  {basic.dateOfBirth
                    ? new Date(basic.dateOfBirth).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                    : "Not specified"}
                </p>
              </div>
              <div>
                <label
                  className="block font-medium text-gray-500"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Age
                </label>
                <p
                  className="text-gray-900 font-medium"
                  style={{
                    fontSize: window.innerWidth < 768 ? "12px" : "14px",
                  }}
                >
                  {basic.dateOfBirth
                    ? new Date().getFullYear() -
                    new Date(basic.dateOfBirth).getFullYear()
                    : "N/A"}{" "}
                  years
                </p>
              </div>
              <div>
                <label
                  className="block font-medium text-gray-500"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Nationality
                </label>
                <p
                  className="text-gray-900 font-medium"
                  style={{
                    fontSize: window.innerWidth < 768 ? "12px" : "14px",
                  }}
                >
                  {basic.nationality || "Not specified"}
                </p>
              </div>
              <div>
                <label
                  className="block font-medium text-gray-500"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Marital Status
                </label>
                <p
                  className="text-gray-900 font-medium"
                  style={{
                    fontSize: window.innerWidth < 768 ? "12px" : "14px",
                  }}
                >
                  {basic.maritalStatus || "Not specified"}
                </p>
              </div>
              <div>
                <label
                  className="block font-medium text-gray-500"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Occupation
                </label>
                <p
                  className="text-gray-900 font-medium"
                  style={{
                    fontSize: window.innerWidth < 768 ? "12px" : "14px",
                  }}
                >
                  {basic.occupation || "Not specified"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact & Address - Two Column Layout */}
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns:
              window.innerWidth < 768 ? "1fr" : "repeat(2, 1fr)",
          }}
        >
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div
              className="border-b border-gray-200"
              style={{
                padding: window.innerWidth < 768 ? "10px 12px" : "12px 24px",
              }}
            >
              <h4
                className="font-semibold text-gray-800 uppercase tracking-wide"
                style={{
                  fontSize: window.innerWidth < 768 ? "11px" : "14px",
                }}
              >
                Contact Information
              </h4>
            </div>
            <div
              className="space-y-3"
              style={{
                padding: window.innerWidth < 768 ? "12px" : "24px",
              }}
            >
              <div>
                <label
                  className="block font-medium text-gray-500"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Primary Phone
                </label>
                <p
                  className="text-gray-900 font-medium flex items-center gap-2"
                  style={{
                    fontSize: window.innerWidth < 768 ? "12px" : "14px",
                  }}
                >
                  <Bell
                    className={
                      window.innerWidth < 768
                        ? "w-3 h-3 text-red-600"
                        : "w-4 h-4 text-red-600"
                    }
                  />
                  {basic.phone || "Not specified"}
                </p>
              </div>
              <div>
                <label
                  className="block font-medium text-gray-500"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Alternate Phone
                </label>
                <p
                  className="text-gray-900 font-medium flex items-center gap-2"
                  style={{
                    fontSize: window.innerWidth < 768 ? "12px" : "14px",
                  }}
                >
                  <Bell
                    className={
                      window.innerWidth < 768
                        ? "w-3 h-3 text-gray-400"
                        : "w-4 h-4 text-gray-400"
                    }
                  />
                  {basic.alternatePhone || "Not specified"}
                </p>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div
              className="border-b border-gray-200 bg-red-50"
              style={{
                padding: window.innerWidth < 768 ? "10px 12px" : "12px 24px",
              }}
            >
              <h4
                className="font-semibold text-red-700 uppercase tracking-wide flex items-center gap-2"
                style={{
                  fontSize: window.innerWidth < 768 ? "11px" : "14px",
                }}
              >
                <AlertCircle
                  className={window.innerWidth < 768 ? "w-3 h-3" : "w-4 h-4"}
                />
                Emergency Contact
              </h4>
            </div>
            <div
              className="space-y-3"
              style={{
                padding: window.innerWidth < 768 ? "12px" : "24px",
              }}
            >
              <div>
                <label
                  className="block font-medium text-gray-500"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Contact Name
                </label>
                <p
                  className="text-gray-900 font-medium"
                  style={{
                    fontSize: window.innerWidth < 768 ? "12px" : "14px",
                  }}
                >
                  {basic.emergencyContactName || "Not specified"}
                </p>
              </div>
              <div>
                <label
                  className="block font-medium text-gray-500"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Contact Phone
                </label>
                <p
                  className="text-gray-900 font-medium"
                  style={{
                    fontSize: window.innerWidth < 768 ? "12px" : "14px",
                  }}
                >
                  {basic.emergencyContactPhone || "Not specified"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div
            className="border-b border-gray-200"
            style={{
              padding: window.innerWidth < 768 ? "10px 12px" : "12px 24px",
            }}
          >
            <h4
              className="font-semibold text-gray-800 uppercase tracking-wide"
              style={{
                fontSize: window.innerWidth < 768 ? "11px" : "14px",
              }}
            >
              Address Information
            </h4>
          </div>
          <div
            style={{
              padding: window.innerWidth < 768 ? "12px" : "24px",
            }}
          >
            <div
              className="grid grid-cols-1"
              style={{ gap: window.innerWidth < 768 ? "12px" : "16px" }}
            >
              <div>
                <label
                  className="block font-medium text-gray-500"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Address
                </label>
                <p
                  className="text-gray-900 font-medium"
                  style={{
                    fontSize: window.innerWidth < 768 ? "12px" : "14px",
                  }}
                >
                  {basic.address || "Not specified"}
                </p>
              </div>
              <div
                className="grid"
                style={{
                  gridTemplateColumns:
                    window.innerWidth < 768
                      ? "repeat(2, 1fr)"
                      : "repeat(4, 1fr)",
                  gap: window.innerWidth < 768 ? "12px 8px" : "16px",
                }}
              >
                <div>
                  <label
                    className="block font-medium text-gray-500"
                    style={{
                      fontSize: window.innerWidth < 768 ? "10px" : "12px",
                      marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                    }}
                  >
                    City
                  </label>
                  <p
                    className="text-gray-900 font-medium"
                    style={{
                      fontSize: window.innerWidth < 768 ? "12px" : "14px",
                    }}
                  >
                    {basic.city || "Not specified"}
                  </p>
                </div>
                <div>
                  <label
                    className="block font-medium text-gray-500"
                    style={{
                      fontSize: window.innerWidth < 768 ? "10px" : "12px",
                      marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                    }}
                  >
                    State
                  </label>
                  <p
                    className="text-gray-900 font-medium"
                    style={{
                      fontSize: window.innerWidth < 768 ? "12px" : "14px",
                    }}
                  >
                    {basic.state || "Not specified"}
                  </p>
                </div>
                <div>
                  <label
                    className="block font-medium text-gray-500"
                    style={{
                      fontSize: window.innerWidth < 768 ? "10px" : "12px",
                      marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                    }}
                  >
                    Pincode
                  </label>
                  <p
                    className="text-gray-900 font-medium"
                    style={{
                      fontSize: window.innerWidth < 768 ? "12px" : "14px",
                    }}
                  >
                    {basic.pincode || "Not specified"}
                  </p>
                </div>
                <div>
                  <label
                    className="block font-medium text-gray-500"
                    style={{
                      fontSize: window.innerWidth < 768 ? "10px" : "12px",
                      marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                    }}
                  >
                    Country
                  </label>
                  <p
                    className="text-gray-900 font-medium"
                    style={{
                      fontSize: window.innerWidth < 768 ? "12px" : "14px",
                    }}
                  >
                    {basic.country || "Not specified"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Membership Status - Compact */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div
            className="bg-gradient-to-r from-red-500 to-red-600"
            style={{
              padding: window.innerWidth < 768 ? "10px 12px" : "12px 24px",
            }}
          >
            <div className="flex items-center justify-between">
              <h4
                className="font-semibold text-white uppercase tracking-wide flex items-center gap-2"
                style={{
                  fontSize: window.innerWidth < 768 ? "11px" : "14px",
                }}
              >
                <Crown
                  className={
                    window.innerWidth < 768
                      ? "w-3 h-3 text-yellow-300"
                      : "w-4 h-4 text-yellow-300"
                  }
                />
                Membership Status
              </h4>
              <span
                className={`px-3 py-1 rounded-full font-semibold ${userSubscription.isActive
                  ? "bg-green-500 text-white"
                  : "bg-white/20 text-white"
                  }`}
                style={{
                  fontSize: window.innerWidth < 768 ? "10px" : "12px",
                  padding: window.innerWidth < 768 ? "4px 10px" : "6px 12px",
                }}
              >
                {userSubscription.isActive ? "Active" : "Basic"}
              </span>
            </div>
          </div>
          <div
            style={{
              padding: window.innerWidth < 768 ? "12px" : "16px 24px",
            }}
          >
            <div
              className="grid"
              style={{
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: window.innerWidth < 768 ? "12px" : "24px",
              }}
            >
              <div className="text-center">
                <p
                  className="text-gray-500 font-medium"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Current Plan
                </p>
                <p
                  className="font-bold text-gray-900"
                  style={{
                    fontSize: window.innerWidth < 768 ? "14px" : "18px",
                  }}
                >
                  {userSubscription.planName === "BASIC"
                    ? "Basic"
                    : userSubscription.planName || "Basic"}
                </p>
              </div>
              <div
                className="text-center"
                style={{
                  borderLeft: "1px solid #E5E7EB",
                  borderRight: "1px solid #E5E7EB",
                }}
              >
                <p
                  className="text-gray-500 font-medium"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Loyalty Points
                </p>
                <p
                  className="font-bold text-yellow-600"
                  style={{
                    fontSize: window.innerWidth < 768 ? "14px" : "18px",
                  }}
                >
                  {userSubscription.loyaltyPoints || 0}
                </p>
              </div>
              <div className="text-center">
                <p
                  className="text-gray-500 font-medium"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                    marginBottom: window.innerWidth < 768 ? "3px" : "4px",
                  }}
                >
                  Total Bookings
                </p>
                <p
                  className="font-bold text-gray-900"
                  style={{
                    fontSize: window.innerWidth < 768 ? "14px" : "18px",
                  }}
                >
                  {userSubscription.totalBookings || 0}
                </p>
              </div>
            </div>
            {userSubscription.memberSince && (
              <div
                className="border-t border-gray-200 text-center"
                style={{
                  marginTop: window.innerWidth < 768 ? "12px" : "16px",
                  paddingTop: window.innerWidth < 768 ? "12px" : "16px",
                }}
              >
                <p
                  className="text-gray-600"
                  style={{
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                  }}
                >
                  Member since{" "}
                  <span className="font-semibold text-gray-900">
                    {new Date(userSubscription.memberSince).toLocaleDateString(
                      "en-IN",
                      {
                        month: "long",
                        year: "numeric",
                      },
                    )}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderBookings = () => {
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const formatTime = (timeString) => {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    };

    const isBookingCompleted = (bookingDate, bookingTime = null) => {
      const today = new Date();
      const booking = new Date(bookingDate);
      if (bookingTime) {
        const [hours, minutes] = bookingTime.split(":");
        booking.setHours(parseInt(hours), parseInt(minutes));
      } else {
        booking.setHours(23, 59, 59);
      }
      return booking < today;
    };

    return (
      <div className="space-y-6">
        {/* Elegant Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="space-y-1">
            <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
              <Calendar className="w-6 h-6 text-red-500" />
              My Bookings
            </h3>
            <p className="text-sm text-gray-500 font-medium">
              You have <span className="text-red-600 font-bold">{(userBookings.roomBookings?.length || 0) + (userBookings.restaurantBookings?.length || 0)}</span> active and past reservations
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchUserBookings}
              disabled={loadingBookings}
              className="group flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-700 text-sm font-bold shadow-sm hover:border-red-200 hover:text-red-600 transition-all duration-300 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loadingBookings ? "animate-spin" : "group-hover:rotate-180 transition-transform duration-500"}`} />
              Refresh
            </button>
            <button
              onClick={() => navigate("/HotelRoomsPage")}
              className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white text-sm font-bold rounded-xl shadow-lg shadow-red-200 hover:shadow-red-300 transform hover:-translate-y-0.5 transition-all duration-300"
            >
              New Booking
            </button>
          </div>
        </div>

        {/* Premium Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Hotel Stays', value: userBookings.totalRoomBookings, icon: Hotel, color: 'from-orange-500 to-red-600', shadow: 'shadow-red-100' },
            { label: 'Restaurants', value: userBookings.totalRestaurantBookings, icon: Utensils, color: 'from-amber-400 to-orange-500', shadow: 'shadow-orange-100' },
            {
              label: 'Upcoming',
              value: (() => {
                const all = [...(userBookings.roomBookings || []), ...(userBookings.restaurantBookings || [])];
                return all.filter(b => b.status !== "CANCELLED" && (b.checkOut ? !isBookingCompleted(b.checkOut) : b.bookingDate ? !isBookingCompleted(b.bookingDate, b.bookingTime) : false)).length;
              })(),
              icon: Clock,
              color: 'from-emerald-400 to-green-600',
              shadow: 'shadow-green-100'
            },
            {
              label: 'Cancelled',
              value: (() => {
                const all = [...(userBookings.roomBookings || []), ...(userBookings.restaurantBookings || [])];
                return all.filter(b => b.status === "CANCELLED").length;
              })(),
              icon: XCircle,
              color: 'from-slate-500 to-slate-700',
              shadow: 'shadow-slate-100'
            }
          ].map((stat, idx) => (
            <div key={idx} className={`relative group p-4 rounded-2xl bg-gradient-to-br ${stat.color} ${stat.shadow} shadow-xl overflow-hidden transform transition-all duration-300 hover:scale-[1.02]`}>
              <div className="absolute -right-2 -top-2 opacity-10 group-hover:scale-110 transition-transform duration-500">
                <stat.icon className="w-24 h-24 text-white" />
              </div>
              <div className="relative z-10 flex flex-col justify-between h-full">
                <div className="bg-white/20 backdrop-blur-md w-10 h-10 rounded-xl flex items-center justify-center mb-3">
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-2xl font-black text-white">{stat.value}</h4>
                  <p className="text-[11px] font-bold text-white/80 uppercase tracking-wider">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Content Section with Integrated Search */}
        <div className="bg-white rounded-3xl shadow-xl shadow-gray-100/50 border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-50 bg-gray-50/30">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h4 className="font-extrabold text-gray-900">Booking History</h4>

              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={bookingSearchQuery}
                  onChange={(e) => setBookingSearchQuery(e.target.value)}
                  placeholder="Search by hotel, date, or status..."
                  className="w-full pl-11 pr-10 py-2.5 bg-white border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-4 focus:ring-red-500/10 focus:border-red-500 transition-all duration-300"
                />
                {bookingSearchQuery && (
                  <button onClick={() => setBookingSearchQuery("")} className="absolute right-4 top-1/2 -translate-y-1/2">
                    <X className="w-4 h-4 text-gray-400 hover:text-red-500" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {(() => {
              const allBookings = [
                ...(userBookings.roomBookings?.map(b => ({ ...b, type: 'hotel' })) || []),
                ...(userBookings.restaurantBookings?.map(b => ({ ...b, type: 'restaurant' })) || [])
              ].filter(booking => {
                if (!bookingSearchQuery) return true;
                const query = bookingSearchQuery.toLowerCase();
                const name = (booking.hotelName || booking.restaurantName || `Reservation ${booking.id || booking.bookingId}`).toLowerCase();
                const date = new Date(booking.checkIn || booking.bookingDate).toLocaleDateString("en-IN", { month: 'short', day: '2-digit', year: 'numeric' }).toLowerCase();
                return name.includes(query) || date.includes(query) || (booking.status || "").toLowerCase().includes(query);
              }).sort((a, b) => new Date(b.createdAt || b.bookingDate) - new Date(a.createdAt || a.bookingDate));

              const startIndex = (currentPage - 1) * itemsPerPage;
              const currentBookings = allBookings.slice(startIndex, startIndex + itemsPerPage);

              if (currentBookings.length === 0 && !loadingBookings) {
                return (
                  <div className="py-20 flex flex-col items-center justify-center text-center px-4">
                    <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-4">
                      <Calendar className="w-10 h-10 text-red-500" />
                    </div>
                    <h5 className="text-lg font-bold text-gray-900">No bookings found</h5>
                    <p className="text-sm text-gray-500 max-w-xs">We couldn't find any reservations matching your criteria. Try adjusting your search.</p>
                  </div>
                );
              }

              return currentBookings.map((booking) => (
                <div key={`${booking.type}-${booking.id || booking.bookingId}`} className="group p-5 hover:bg-gray-50/50 transition-all duration-300">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                    {/* Icon & Name Section */}
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`p-3 rounded-2xl shadow-sm ${booking.type === 'hotel' ? 'bg-orange-50 text-orange-600' : 'bg-amber-50 text-amber-600'}`}>
                        {booking.type === 'hotel' ? <Hotel className="w-6 h-6" /> : <Utensils className="w-6 h-6" />}
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${booking.type === 'hotel' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'}`}>
                            {booking.type}
                          </span>
                          <span className="text-xs text-gray-400 font-medium">#{booking.pnr || booking.id || booking.bookingId}</span>
                        </div>
                        <h5 className="font-extrabold text-gray-900 group-hover:text-red-600 transition-colors duration-300 text-base">
                          {booking.hotelName || booking.restaurantName || "Premium Reservation"}
                        </h5>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 font-medium">
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-red-400" />
                            <span className="truncate max-w-[200px]">{booking.hotelAddress || booking.restaurantAddress || 'Jodhpur, Rajasthan'}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5 text-blue-400" />
                            <span>{booking.guests || booking.guestCount} {booking.guests > 1 ? 'Guests' : 'Guest'}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Timeline Section */}
                    <div className="flex items-center gap-6 flex-shrink-0">
                      <div className="flex flex-col items-center">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Check In</p>
                        <p className="text-sm font-black text-gray-800">{formatDate(booking.checkIn || booking.bookingDate)}</p>
                        <p className="text-[11px] font-bold text-red-500">{booking.checkInTime || booking.bookingTime || '12:00 PM'}</p>
                      </div>
                      <div className="w-8 h-[2px] bg-gray-100 relative">
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-gray-200 rounded-full"></div>
                      </div>
                      <div className="flex flex-col items-center">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Check Out</p>
                        <p className="text-sm font-black text-gray-800">{booking.checkOut ? formatDate(booking.checkOut) : 'N/A'}</p>
                        <p className="text-[11px] font-bold text-gray-400">{booking.checkOutTime || '11:00 AM'}</p>
                      </div>
                    </div>

                    {/* Status & Pricing Section */}
                    <div className="flex items-center justify-between lg:justify-end gap-6 lg:w-64">
                      <div className="text-right">
                        <p className="text-xl font-black text-gray-900">₹{booking.totalAmount?.toLocaleString() || '---'}</p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Payable</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {(() => {
                          const completed = isBookingCompleted(booking.checkOut || booking.bookingDate);
                          const cancelled = booking.status === "CANCELLED";
                          let color = "bg-green-100 text-green-700";
                          let label = "Upcoming";
                          let icon = <Clock className="w-3 h-3" />;

                          if (cancelled) {
                            color = "bg-red-100 text-red-700";
                            label = "Cancelled";
                            icon = <XCircle className="w-3 h-3" />;
                          } else if (completed) {
                            color = "bg-blue-100 text-blue-700";
                            label = "Completed";
                            icon = <CheckCircle2 className="w-3 h-3" />;
                          }

                          return (
                            <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${color}`}>
                              {icon}
                              {label}
                            </span>
                          );
                        })()}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDownloadReceipt(booking)}
                            className="p-2 bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-500 rounded-xl transition-all duration-300 shadow-sm"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {booking.status !== "CANCELLED" && !isBookingCompleted(booking.checkOut || booking.bookingDate) && (
                            <button
                              onClick={() => handleCancelBooking(booking, booking.type)}
                              className="p-2 bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all duration-300 shadow-sm"
                              title="Cancel Reservation"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>

          {/* Pagination */}
          {(() => {
            const all = [
              ...(userBookings.roomBookings || []),
              ...(userBookings.restaurantBookings || [])
            ].filter(booking => {
              if (!bookingSearchQuery) return true;
              const query = bookingSearchQuery.toLowerCase();
              const name = (booking.hotelName || booking.restaurantName || "").toLowerCase();
              return name.includes(query);
            });
            const totalPages = Math.ceil(all.length / itemsPerPage);
            if (totalPages <= 1) return null;

            return (
              <div className="px-6 py-4 bg-gray-50/50 flex items-center justify-center gap-4">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  className="p-2 bg-white border border-gray-200 rounded-xl text-gray-500 hover:text-red-500 disabled:opacity-30 transition-all shadow-sm"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`w-8 h-8 rounded-xl text-xs font-bold transition-all duration-300 ${currentPage === i + 1 ? 'bg-red-600 text-white shadow-lg shadow-red-200' : 'bg-white text-gray-500 border border-gray-100 hover:border-red-200'}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  className="p-2 bg-white border border-gray-200 rounded-xl text-gray-500 hover:text-red-500 disabled:opacity-30 transition-all shadow-sm"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            );
          })()}
        </div>

        {/* Loading Overlay */}
        {loadingBookings && (
          <div className="fixed inset-0 bg-white/60 backdrop-blur-sm z-[100] flex flex-col items-center justify-center animate-in fade-in duration-300">
            <div className="w-16 h-16 bg-white rounded-3xl shadow-2xl flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
            </div>
            <p className="font-black text-gray-900 uppercase tracking-widest text-xs">Loading Experience...</p>
          </div>
        )}
      </div>
    );
  };

  const renderPayments = () => {
    return (
      <div className="space-y-4">
        {/* Compact Header */}
        <div className="flex justify-between items-center">
          <h3 className="text-2xl font-bold bg-gradient-to-r from-red-600 to-yellow-600 bg-clip-text text-transparent">
            Payment Information
          </h3>
          <button
            onClick={fetchUserPayments}
            disabled={loadingPayments}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white text-sm rounded-xl hover:from-red-600 hover:to-red-700 disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-semibold"
          >
            <RefreshCw
              className={`w-4 h-4 ${loadingPayments ? "animate-spin" : ""}`}
            />
            {loadingPayments ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {loadingPayments ? (
          <div className="text-center py-12">
            <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
              <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
            <p className="text-gray-600 font-medium">
              Loading payment details...
            </p>
          </div>
        ) : (
          <>
            {/* Compact Payment Summary - 2 Cards Only */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Total Spent - Large Premium Card */}
              <div className="group relative bg-gradient-to-br from-red-500 via-red-600 to-yellow-600 p-4 rounded-xl border-2 border-red-300 hover:border-yellow-400 transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 shadow-xl hover:shadow-2xl overflow-hidden">
                {/* Animated Background */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-400 rounded-full -translate-y-12 translate-x-12 animate-pulse"></div>
                  <div
                    className="absolute bottom-0 left-0 w-20 h-20 bg-red-400 rounded-full translate-y-10 -translate-x-10 animate-pulse"
                    style={{ animationDelay: "0.5s" }}
                  ></div>
                </div>

                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-2">
                    <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg shadow-lg">
                      <CreditCard className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-black text-white mb-1 drop-shadow-2xl">
                      ₹{userPayments.totalSpent?.toLocaleString("en-IN") || 0}
                    </div>
                    <div className="text-xs text-white/90 font-bold uppercase tracking-widest">
                      Total Spent
                    </div>
                  </div>
                </div>

                {/* Shine Effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 translate-x-full group-hover:-translate-x-full transition-transform duration-1000"></div>
                </div>
              </div>

              {/* Last Payment Method - Compact Card */}
              <div className="group bg-gradient-to-br from-white to-red-50 p-4 rounded-xl border-2 border-red-200 hover:border-red-400 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
                <div className="flex items-center gap-2 mb-2">
                  <div className="bg-gradient-to-br from-red-500 to-red-600 p-2 rounded-lg shadow-md">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <label className="text-xs font-bold text-red-700 uppercase tracking-wider">
                    Last Payment Method
                  </label>
                </div>
                <p className="text-xl font-bold text-gray-900 truncate">
                  {userPayments.lastUsedMethod || "No payments yet"}
                </p>
                {userPayments.preferredMethod && (
                  <p className="text-xs text-gray-600 mt-1">
                    Preferred:{" "}
                    <span className="font-semibold text-red-600">
                      {userPayments.preferredMethod}
                    </span>
                  </p>
                )}
              </div>
            </div>

            {/* Compact Payment Features - 2x2 Grid */}
            <div className="bg-white rounded-2xl p-5 shadow-lg border border-red-100">
              <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-600" />
                Payment Features
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-start gap-3 p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 hover:shadow-md transition-all duration-200">
                  <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                    <span className="text-white text-lg font-bold">✓</span>
                  </div>
                  <div>
                    <h5 className="font-bold text-gray-900 text-sm mb-0.5">
                      Auto-Save
                    </h5>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Methods saved on successful transactions
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gradient-to-br from-red-50 to-red-100 rounded-xl border border-red-200 hover:shadow-md transition-all duration-200">
                  <div className="w-9 h-9 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                    <span className="text-white text-lg">🔒</span>
                  </div>
                  <div>
                    <h5 className="font-bold text-gray-900 text-sm mb-0.5">
                      Secure
                    </h5>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      No sensitive data stored
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200 hover:shadow-md transition-all duration-200">
                  <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                    <span className="text-white text-lg">💳</span>
                  </div>
                  <div>
                    <h5 className="font-bold text-gray-900 text-sm mb-0.5">
                      Multiple
                    </h5>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      UPI, Cards & more options
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl border border-yellow-200 hover:shadow-md transition-all duration-200">
                  <div className="w-9 h-9 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                    <span className="text-white text-lg">📊</span>
                  </div>
                  <div>
                    <h5 className="font-bold text-gray-900 text-sm mb-0.5">
                      Insights
                    </h5>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Track all your spending
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        paddingTop: window.innerWidth < 768 ? "70px" : "90px",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #FFF5F5 0%, #FFF9E6 100%)",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        paddingBottom: window.innerWidth < 768 ? "30px" : "50px",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: window.innerWidth < 768 ? "0 8px" : "0 12px",
        }}
      >
        {/* Page Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              window.innerWidth < 768 ? "flex-start" : "space-between",
            marginBottom: window.innerWidth < 768 ? "12px" : "20px",
            flexWrap: "wrap",
            gap: window.innerWidth < 768 ? "8px" : "10px",
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: window.innerWidth < 768 ? "4px" : "6px",
              padding: window.innerWidth < 768 ? "6px 10px" : "8px 12px",
              background: "rgba(238, 46, 36, 0.1)",
              border: "1px solid rgba(238, 46, 36, 0.2)",
              borderRadius: window.innerWidth < 768 ? "8px" : "10px",
              color: "#EE2E24",
              fontSize: window.innerWidth < 768 ? "12px" : "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(238, 46, 36, 0.15)";
              e.currentTarget.style.transform = "translateX(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(238, 46, 36, 0.1)";
              e.currentTarget.style.transform = "translateX(0)";
            }}
          >
            <ArrowLeft size={window.innerWidth < 768 ? 16 : 18} />
            {window.innerWidth < 768 ? "" : "Back"}
          </button>
          <h1
            style={{
              margin: 0,
              fontSize: window.innerWidth < 768 ? "20px" : "32px",
              fontWeight: "700",
              background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              flex: window.innerWidth < 768 ? 1 : "initial",
            }}
          >
            My Profile
          </h1>
          <div style={{ width: window.innerWidth < 768 ? "0" : "100px" }} />
        </div>

        {/* Main Content Container */}
        <div
          style={{
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: window.innerWidth < 768 ? "12px" : "20px",
            boxShadow:
              window.innerWidth < 768
                ? "0 8px 20px rgba(238, 46, 36, 0.08)"
                : "0 20px 40px rgba(238, 46, 36, 0.1)",
            border: "1px solid rgba(238, 46, 36, 0.1)",
            overflow: "hidden",
          }}
        >
          {/* Tabs and Content Container */}
          <div
            style={{
              display: "flex",
              flexDirection: window.innerWidth < 768 ? "column" : "row",
              minHeight: window.innerWidth < 768 ? "auto" : "600px",
            }}
          >
            <div
              className="border-r border-red-200/60 flex-shrink-0 bg-gradient-to-b from-red-100/90 via-red-50/80 to-white/90 backdrop-blur-lg"
              style={{
                width: window.innerWidth < 768 ? "100%" : "auto",
                borderRight: window.innerWidth < 768 ? "none" : undefined,
                borderBottom:
                  window.innerWidth < 768
                    ? "1px solid rgba(239, 68, 68, 0.3)"
                    : undefined,
              }}
            >
              <nav
                className="overflow-y-auto"
                style={{
                  display: window.innerWidth < 768 ? "flex" : "block",
                  flexWrap: window.innerWidth < 768 ? "wrap" : undefined,
                  gap: window.innerWidth < 768 ? "6px" : undefined,
                  padding: window.innerWidth < 768 ? "10px" : "12px",
                }}
              >
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isMobile = window.innerWidth < 768;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center transition-all duration-200 shadow-sm ${activeTab === tab.id
                        ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-red-300/60 scale-[1.02] border border-red-400/60"
                        : "bg-white/80 text-red-700 hover:bg-red-100/90 border border-red-100/40"
                        }`}
                      style={{
                        width: isMobile ? "auto" : "100%",
                        marginBottom: isMobile ? "0" : "6px",
                        flexShrink: isMobile ? 0 : undefined,
                        whiteSpace: isMobile ? "nowrap" : undefined,
                        padding: isMobile ? "6px 10px" : "8px 12px",
                        gap: isMobile ? "4px" : "10px",
                        fontSize: isMobile ? "10px" : "12px",
                        fontWeight: "600",
                        borderRadius: isMobile ? "8px" : "10px",
                      }}
                    >
                      <Icon className={isMobile ? "w-3.5 h-3.5" : "w-4 h-4"} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            <div
              className="flex-1 overflow-y-auto bg-gradient-to-br from-white/95 via-blue-50/60 to-blue-100/50"
              style={{ padding: window.innerWidth < 768 ? "12px" : "24px" }}
            >
              {activeTab === "basic" && renderBasicInfo()}
              {activeTab === "bookings" && renderBookings()}
              {activeTab === "payments" && renderPayments()}
              {activeTab === "preferences" && (
                <div className="space-y-5">
                  <h3 className="text-xl font-bold text-red-700 mb-1">
                    My Preferences
                  </h3>

                  {loadingPreferences ? (
                    <div className="flex items-center justify-center py-12 bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl shadow-md">
                      <Loader2 className="h-6 w-6 animate-spin mr-2 text-red-600" />
                      <span className="text-gray-700 font-medium">
                        Loading preferences...
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {/* Room Preferences */}
                      <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-5 shadow-md">
                        <h4 className="text-base font-bold text-red-700 mb-4 flex items-center">
                          <BedDouble className="w-5 h-5 mr-2" />
                          Room Preferences
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-red-600">
                              Preferred Room Type
                            </label>
                            <select
                              value={
                                userPreferences.preferredRoomType || "Standard"
                              }
                              onChange={(e) =>
                                handlePreferenceChange(
                                  "preferredRoomType",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="Standard">Standard</option>
                              <option value="Deluxe">Deluxe</option>
                              <option value="Suite">Suite</option>
                              <option value="Executive">Executive</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-red-600">
                              AC Preference
                            </label>
                            <select
                              value={userPreferences.acPreference || "AC"}
                              onChange={(e) =>
                                handlePreferenceChange(
                                  "acPreference",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="AC">AC</option>
                              <option value="Non-AC">Non-AC</option>
                              <option value="Any">Any</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-red-600">
                              Bed Preference
                            </label>
                            <select
                              value={userPreferences.bedPreference || "Double"}
                              onChange={(e) =>
                                handlePreferenceChange(
                                  "bedPreference",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="Single">Single</option>
                              <option value="Double">Double</option>
                              <option value="Queen">Queen</option>
                              <option value="King">King</option>
                              <option value="Twin">Twin</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-red-600">
                              View Preference
                            </label>
                            <select
                              value={userPreferences.viewPreference || "City"}
                              onChange={(e) =>
                                handlePreferenceChange(
                                  "viewPreference",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="Any">Any</option>
                              <option value="City">City</option>
                              <option value="Sea">Sea</option>
                              <option value="Garden">Garden</option>
                              <option value="Pool">Pool</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-red-600">
                              Floor Preference
                            </label>
                            <select
                              value={userPreferences.floorPreference || "Mid"}
                              onChange={(e) =>
                                handlePreferenceChange(
                                  "floorPreference",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="Any">Any</option>
                              <option value="Low">Low (1-3)</option>
                              <option value="Mid">Mid (4-7)</option>
                              <option value="High">High (8+)</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Food Preferences */}
                      <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-5 shadow-md">
                        <h4 className="text-base font-bold text-red-700 mb-4 flex items-center">
                          <Utensils className="w-5 h-5 mr-2" />
                          Food Preferences
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-red-600">
                              Food Preference
                            </label>
                            <select
                              value={
                                userPreferences.foodPreference || "Vegetarian"
                              }
                              onChange={(e) =>
                                handlePreferenceChange(
                                  "foodPreference",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="Vegetarian">Vegetarian</option>
                              <option value="Non-Vegetarian">
                                Non-Vegetarian
                              </option>
                              <option value="Vegan">Vegan</option>
                              <option value="Jain">Jain</option>
                              <option value="Eggetarian">Eggetarian</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-red-600">
                              Dietary Restrictions
                            </label>
                            <select
                              value={
                                userPreferences.dietaryRestrictions || "None"
                              }
                              onChange={(e) =>
                                handlePreferenceChange(
                                  "dietaryRestrictions",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="None">None</option>
                              <option value="Gluten-Free">Gluten-Free</option>
                              <option value="Dairy-Free">Dairy-Free</option>
                              <option value="Nut Allergy">Nut Allergy</option>
                              <option value="Low Sugar">Low Sugar</option>
                              <option value="Low Salt">Low Salt</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-red-600">
                              Cuisine Preference
                            </label>
                            <select
                              value={
                                userPreferences.cuisinePreference || "Indian"
                              }
                              onChange={(e) =>
                                handlePreferenceChange(
                                  "cuisinePreference",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="Indian">Indian</option>
                              <option value="Chinese">Chinese</option>
                              <option value="Continental">Continental</option>
                              <option value="Italian">Italian</option>
                              <option value="Mexican">Mexican</option>
                              <option value="Thai">Thai</option>
                              <option value="Multi-Cuisine">
                                Multi-Cuisine
                              </option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-red-600">
                              Meal Plan
                            </label>
                            <select
                              value={
                                userPreferences.mealPlan || "Breakfast + Dinner"
                              }
                              onChange={(e) =>
                                handlePreferenceChange(
                                  "mealPlan",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="Room Only">Room Only</option>
                              <option value="Breakfast Only">
                                Breakfast Only
                              </option>
                              <option value="Breakfast + Lunch">
                                Breakfast + Lunch
                              </option>
                              <option value="Breakfast + Dinner">
                                Breakfast + Dinner
                              </option>
                              <option value="All Meals">All Meals</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Special Requirements */}
                      <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-5 shadow-md">
                        <h4 className="text-base font-bold text-red-700 mb-4 flex items-center">
                          <AlertCircle className="w-5 h-5 mr-2" />
                          Special Requirements
                        </h4>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-red-600">
                              Special Requests
                            </label>
                            <textarea
                              value={userPreferences.specialRequests || ""}
                              onChange={(e) =>
                                handlePreferenceChange(
                                  "specialRequests",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter any special requests..."
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm min-h-[60px] resize-vertical"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-red-600">
                              Accessibility Needs
                            </label>
                            <select
                              value={
                                userPreferences.accessibilityNeeds || "None"
                              }
                              onChange={(e) =>
                                handlePreferenceChange(
                                  "accessibilityNeeds",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="None">None</option>
                              <option value="Wheelchair Access">
                                Wheelchair Access
                              </option>
                              <option value="Hearing Assistance">
                                Hearing Assistance
                              </option>
                              <option value="Visual Assistance">
                                Visual Assistance
                              </option>
                              <option value="Ground Floor Access">
                                Ground Floor Access
                              </option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-red-600">
                              Loyalty Program
                            </label>
                            <select
                              value={
                                userPreferences.loyaltyProgramEnrollment
                                  ? "true"
                                  : "false"
                              }
                              onChange={(e) =>
                                handlePreferenceChange(
                                  "loyaltyProgramEnrollment",
                                  e.target.value === "true",
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="true">Enrolled</option>
                              <option value="false">Not Enrolled</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-end gap-3 pt-4 border-t border-red-200/60">
                        <button
                          onClick={fetchUserPreferences}
                          disabled={loadingPreferences}
                          className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 font-semibold rounded-lg hover:bg-red-50 transition-all duration-200"
                        >
                          <RefreshCw
                            className={`w-4 h-4 ${loadingPreferences ? "animate-spin" : ""
                              }`}
                          />
                          {loadingPreferences ? "Refreshing..." : "Refresh"}
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          className="inline-flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Save className="w-4 h-4" />
                          {saving ? "Saving..." : "Save Changes"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {activeTab === "subscription" && (
                <div className="space-y-4">
                  <h3 className="text-base font-bold text-red-700 mb-1">
                    Membership
                  </h3>

                  {loadingSubscription ? (
                    <div className="flex items-center justify-center py-10 bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl shadow-md">
                      <Loader2 className="h-5 w-5 animate-spin mr-2 text-red-600" />
                      <span className="text-gray-700 text-sm font-medium">
                        Loading membership details...
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Membership Status */}
                      <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-4 shadow-md">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-base font-bold text-red-700 flex items-center">
                            <Star className="w-4 h-4 text-yellow-500 mr-1.5" />
                            Membership Status
                          </h4>
                          <span
                            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold shadow-sm ${userSubscription.isActive
                              ? "bg-gradient-to-r from-green-400 to-green-500 text-white"
                              : "bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800"
                              }`}
                          >
                            {userSubscription.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-red-600">
                              Current Plan
                            </label>
                            <div className="p-2.5 bg-white/70 border border-red-100/60 rounded-lg shadow-sm">
                              <span className="text-sm text-gray-800 font-bold">
                                {userSubscription.planName || "No Active Plan"}
                              </span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-red-600">
                              Plan Type
                            </label>
                            <div className="p-2.5 bg-white/70 border border-red-100/60 rounded-lg shadow-sm">
                              <span className="text-sm text-gray-800 font-bold">
                                {userSubscription.planType || "BASIC"}
                              </span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-red-600">
                              Start Date
                            </label>
                            <div className="p-2.5 bg-white/70 border border-red-100/60 rounded-lg shadow-sm">
                              <span className="text-sm text-gray-800 font-medium">
                                {userSubscription.startDate
                                  ? new Date(
                                    userSubscription.startDate,
                                  ).toLocaleDateString("en-IN", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  })
                                  : "N/A"}
                              </span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-red-600">
                              End Date
                            </label>
                            <div className="p-2.5 bg-white/70 border border-red-100/60 rounded-lg shadow-sm">
                              <span className="text-sm text-gray-800 font-medium">
                                {userSubscription.endDate
                                  ? new Date(
                                    userSubscription.endDate,
                                  ).toLocaleDateString("en-IN", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  })
                                  : "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Membership Statistics */}
                      <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-4 shadow-md">
                        <h4 className="text-base font-bold text-red-700 mb-3">
                          Membership Statistics
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="text-center p-3 bg-gradient-to-br from-blue-100 to-blue-200 border border-red-300/60 rounded-xl shadow-sm transition-all hover:shadow-md hover:-translate-y-1">
                            <div className="text-xl font-bold text-red-700">
                              {userSubscription.totalBookings || 0}
                            </div>
                            <div className="text-xs font-semibold text-red-600">
                              Total Bookings
                            </div>
                          </div>
                          <div className="text-center p-3 bg-gradient-to-br from-green-100 to-green-200 border border-green-300/60 rounded-xl shadow-sm transition-all hover:shadow-md hover:-translate-y-1">
                            <div className="text-xl font-bold text-green-700">
                              {userSubscription.loyaltyPoints || 0}
                            </div>
                            <div className="text-xs font-semibold text-green-600">
                              Loyalty Points
                            </div>
                          </div>
                          <div className="text-center p-3 bg-gradient-to-br from-purple-100 to-purple-200 border border-purple-300/60 rounded-xl shadow-sm transition-all hover:shadow-md hover:-translate-y-1">
                            <div className="text-xl font-bold text-purple-700">
                              {userSubscription.memberSince
                                ? new Date(
                                  userSubscription.memberSince,
                                ).getFullYear()
                                : "N/A"}
                            </div>
                            <div className="text-xs font-semibold text-purple-600">
                              Member Since
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Plan Features */}
                      {userSubscription.features &&
                        userSubscription.features.length > 0 && (
                          <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-4 shadow-md">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-base font-bold text-red-700">
                                Plan Features
                              </h4>
                              <button
                                onClick={() =>
                                  navigate("/membership-plans", {
                                    state: {
                                      currentPlan:
                                        userSubscription.planType ||
                                        userSubscription.membershipTier ||
                                        "Basic",
                                      userId: userId,
                                    },
                                  })
                                }
                                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm hover:shadow-md"
                              >
                                Upgrade Plan
                              </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                              {userSubscription.features.map(
                                (feature, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center space-x-2"
                                  >
                                    <div className="w-1.5 h-1.5 bg-gradient-to-br from-green-400 to-green-500 rounded-full shadow-sm"></div>
                                    <span className="text-xs text-gray-700 font-medium">
                                      {feature}
                                    </span>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}

                      {/* Upgrade Section - Conditional based on plan tier */}
                      {userSubscription.planType !== "PLATINUM" && (
                        <div
                          className={`border rounded-xl p-4 text-center shadow-md transition-all hover:shadow-lg ${!userSubscription.isActive
                            ? "bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-300/60"
                            : "bg-gradient-to-br from-blue-50 to-indigo-50 border-red-300/60"
                            }`}
                        >
                          {!userSubscription.isActive ? (
                            <>
                              <Star className="w-10 h-10 text-yellow-500 mx-auto mb-2.5" />
                              <h4 className="text-base font-bold text-yellow-800 mb-1.5">
                                Basic Membership
                              </h4>
                              <p className="text-xs text-yellow-700 mb-3 font-medium">
                                Upgrade to a premium membership to unlock
                                exclusive benefits and features.
                              </p>
                              <button
                                onClick={() =>
                                  navigate("/membership-plans", {
                                    state: {
                                      currentPlan:
                                        userSubscription.planType ||
                                        userSubscription.membershipTier ||
                                        "Basic",
                                      userId: userId,
                                    },
                                  })
                                }
                                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-4 py-2 rounded-lg font-bold text-xs transition-all shadow-sm hover:shadow-md"
                              >
                                Upgrade Plan
                              </button>
                            </>
                          ) : (
                            <>
                              <Shield className="w-10 h-10 text-blue-500 mx-auto mb-2.5" />
                              <h4 className="text-base font-bold text-red-800 mb-1.5">
                                {userSubscription.planType} Membership Active
                              </h4>
                              <p className="text-xs text-red-700 mb-3 font-medium">
                                {userSubscription.planType === "SILVER"
                                  ? "Upgrade to Gold or Platinum for even more benefits!"
                                  : "Upgrade to Platinum for the ultimate experience!"}
                              </p>
                              <button
                                onClick={() =>
                                  navigate("/membership-plans", {
                                    state: {
                                      currentPlan:
                                        userSubscription.planType ||
                                        userSubscription.membershipTier ||
                                        "Basic",
                                      userId: userId,
                                    },
                                  })
                                }
                                className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-4 py-2 rounded-lg font-bold text-xs transition-all shadow-sm hover:shadow-md"
                              >
                                Upgrade Plan
                              </button>
                            </>
                          )}
                        </div>
                      )}

                      {/* Platinum Member - No Upgrade Needed */}
                      {userSubscription.planType === "PLATINUM" && (
                        <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 border border-purple-300/60 rounded-xl p-4 text-center shadow-md transition-all hover:shadow-lg">
                          <div className="flex items-center justify-center mb-2.5">
                            <Star className="w-6 h-6 text-purple-500 mr-1.5 animate-pulse" />
                            <Crown className="w-8 h-8 text-yellow-500" />
                            <Star className="w-6 h-6 text-purple-500 ml-1.5 animate-pulse" />
                          </div>
                          <h4 className="text-base font-bold text-purple-800 mb-1.5">
                            Platinum Member
                          </h4>
                          <p className="text-xs text-purple-700 mb-3 font-medium">
                            🎉 Congratulations! You have our highest tier
                            membership with all premium benefits.
                          </p>
                          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-400 to-pink-400 text-white rounded-full shadow-sm">
                            <span className="font-bold text-xs">
                              Maximum Benefits Unlocked
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Refresh Button */}
                      <div className="flex justify-end">
                        <button
                          onClick={fetchUserSubscription}
                          disabled={loadingSubscription}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs rounded-lg hover:from-red-600 hover:to-red-700 disabled:opacity-50 shadow-sm hover:shadow-md font-semibold transition-all"
                        >
                          <RefreshCw
                            className={`w-3.5 h-3.5 ${loadingSubscription ? "animate-spin" : ""
                              }`}
                          />
                          {loadingSubscription
                            ? "Refreshing..."
                            : "Refresh Membership"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {activeTab === "settings" && (
                <div className="space-y-4">
                  <h3 className="text-base font-bold text-red-700 mb-1">
                    Settings & Preferences
                  </h3>

                  {loadingSettings ? (
                    <div className="flex items-center justify-center py-10 bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl shadow-md">
                      <Loader2 className="h-5 w-5 animate-spin mr-2 text-red-600" />
                      <span className="text-gray-700 text-sm font-medium">
                        Loading settings...
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Notification Settings */}
                      <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-4 shadow-md">
                        <h4 className="text-base font-bold text-red-700 mb-3 flex items-center">
                          <Bell className="w-4 h-4 mr-1.5" />
                          Notification Preferences
                        </h4>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-2.5 bg-white/60 border border-red-100/60 rounded-lg transition-all hover:shadow-sm">
                            <div>
                              <p className="font-semibold text-gray-800 text-xs">
                                Email Notifications
                              </p>
                              <p className="text-xs text-gray-600 font-medium">
                                Receive booking confirmations and updates via
                                email
                              </p>
                            </div>
                            <input
                              type="checkbox"
                              checked={userSettings.emailNotifications ?? true}
                              onChange={(e) =>
                                handleSettingChange(
                                  "emailNotifications",
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 text-red-600 rounded focus:ring-2 focus:ring-red-400 cursor-pointer"
                            />
                          </div>

                          <div className="flex items-center justify-between p-2.5 bg-white/60 border border-red-100/60 rounded-lg transition-all hover:shadow-sm">
                            <div>
                              <p className="font-semibold text-gray-800 text-xs">
                                SMS Alerts
                              </p>
                              <p className="text-xs text-gray-600 font-medium">
                                Get instant SMS notifications for bookings
                              </p>
                            </div>
                            <input
                              type="checkbox"
                              checked={userSettings.smsNotifications ?? false}
                              onChange={(e) =>
                                handleSettingChange(
                                  "smsNotifications",
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 text-red-600 rounded focus:ring-2 focus:ring-red-400 cursor-pointer"
                            />
                          </div>

                          <div className="flex items-center justify-between p-2.5 bg-white/60 border border-red-100/60 rounded-lg transition-all hover:shadow-sm">
                            <div>
                              <p className="font-semibold text-gray-800 text-xs">
                                Promotional Offers
                              </p>
                              <p className="text-xs text-gray-600 font-medium">
                                Receive deals and special offers
                              </p>
                            </div>
                            <input
                              type="checkbox"
                              checked={userSettings.marketingEmails ?? false}
                              onChange={(e) =>
                                handleSettingChange(
                                  "marketingEmails",
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 text-red-600 rounded focus:ring-2 focus:ring-red-400 cursor-pointer"
                            />
                          </div>

                          <div className="flex items-center justify-between p-2.5 bg-white/60 border border-red-100/60 rounded-lg transition-all hover:shadow-sm">
                            <div>
                              <p className="font-semibold text-gray-800 text-xs">
                                Booking Reminders
                              </p>
                              <p className="text-xs text-gray-600 font-medium">
                                Get reminders about upcoming bookings
                              </p>
                            </div>
                            <input
                              type="checkbox"
                              checked={userSettings.pushNotifications ?? true}
                              onChange={(e) =>
                                handleSettingChange(
                                  "pushNotifications",
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 text-red-600 rounded focus:ring-2 focus:ring-red-400 cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Privacy Settings */}
                      <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-4 shadow-md">
                        <h4 className="text-base font-bold text-red-700 mb-3 flex items-center">
                          <Lock className="w-4 h-4 mr-1.5" />
                          Privacy Settings
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-semibold text-red-600 mb-1.5">
                              Profile Visibility
                            </label>
                            <select
                              value={
                                userSettings.profileVisibility || "PRIVATE"
                              }
                              onChange={(e) =>
                                handleSettingChange(
                                  "profileVisibility",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="PUBLIC">
                                Public - Visible to other users
                              </option>
                              <option value="PRIVATE">
                                Private - Only visible to you
                              </option>
                              <option value="FRIENDS_ONLY">
                                Limited - Visible to friends only
                              </option>
                            </select>
                          </div>

                          <div className="flex items-center justify-between p-2.5 bg-white/60 border border-red-100/60 rounded-lg transition-all hover:shadow-sm">
                            <div>
                              <p className="font-semibold text-gray-800 text-xs">
                                Data Sharing
                              </p>
                              <p className="text-xs text-gray-600 font-medium">
                                Allow us to share your anonymized data for
                                analytics
                              </p>
                            </div>
                            <input
                              type="checkbox"
                              checked={userSettings.dataSharing ?? false}
                              onChange={(e) =>
                                handleSettingChange(
                                  "dataSharing",
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 text-red-600 rounded focus:ring-2 focus:ring-red-400 cursor-pointer"
                            />
                          </div>

                          <div className="flex items-center justify-between p-2.5 bg-white/60 border border-red-100/60 rounded-lg transition-all hover:shadow-sm">
                            <div>
                              <p className="font-semibold text-gray-800 text-xs">
                                Marketing Emails
                              </p>
                              <p className="text-xs text-gray-600 font-medium">
                                Receive promotional offers and updates
                              </p>
                            </div>
                            <input
                              type="checkbox"
                              checked={userSettings.marketingEmails ?? false}
                              onChange={(e) =>
                                handleSettingChange(
                                  "marketingEmails",
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 text-red-600 rounded focus:ring-2 focus:ring-red-400 cursor-pointer"
                            />
                          </div>

                          <div className="flex items-center justify-between p-2.5 bg-white/60 border border-red-100/60 rounded-lg transition-all hover:shadow-sm">
                            <div>
                              <p className="font-semibold text-gray-800 text-xs">
                                Data Sharing for Analytics
                              </p>
                              <p className="text-xs text-gray-600 font-medium">
                                Help improve our services with anonymous usage
                                data
                              </p>
                            </div>
                            <input
                              type="checkbox"
                              checked={formData.dataSharing ?? true}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  dataSharing: e.target.checked,
                                })
                              }
                              className="h-4 w-4 text-red-600 rounded focus:ring-2 focus:ring-red-400 cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Booking Preferences */}
                      <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-4 shadow-md">
                        <h4 className="text-base font-bold text-red-700 mb-3 flex items-center">
                          <CreditCard className="w-4 h-4 mr-1.5" />
                          Booking Preferences
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-semibold text-red-600 mb-1.5">
                              Default Payment Method
                            </label>
                            <select
                              value={
                                userSettings.defaultPaymentMethod || "Card"
                              }
                              onChange={(e) =>
                                handleSettingChange(
                                  "defaultPaymentMethod",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="Card">Credit/Debit Card</option>
                              <option value="UPI">UPI</option>
                              <option value="Wallet">Digital Wallet</option>
                              <option value="Cash">Cash on Arrival</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-red-600 mb-1.5">
                              Auto-fill Guest Information
                            </label>
                            <select
                              value={userSettings.autoFillBookingInfo || "Yes"}
                              onChange={(e) =>
                                handleSettingChange(
                                  "autoFillBookingInfo",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="Yes">
                                Yes - Use my profile info
                              </option>
                              <option value="No">
                                No - Manual entry each time
                              </option>
                              <option value="Ask">Ask me every time</option>
                            </select>
                          </div>

                          <div className="flex items-center justify-between p-2.5 bg-white/60 border border-red-100/60 rounded-lg transition-all hover:shadow-sm">
                            <div>
                              <p className="font-semibold text-gray-800 text-xs">
                                Save Payment Information
                              </p>
                              <p className="text-xs text-gray-600 font-medium">
                                Securely store payment methods for faster
                                checkout
                              </p>
                            </div>
                            <input
                              type="checkbox"
                              checked={userSettings.savedPaymentMethods ?? true}
                              onChange={(e) =>
                                handleSettingChange(
                                  "savedPaymentMethods",
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 text-red-600 rounded focus:ring-2 focus:ring-red-400 cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Security Settings */}
                      <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-4 shadow-md">
                        <h4 className="text-base font-bold text-red-700 mb-3 flex items-center">
                          <Shield className="w-4 h-4 mr-1.5" />
                          Security Settings
                        </h4>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-2.5 bg-white/60 border border-red-100/60 rounded-lg transition-all hover:shadow-sm">
                            <div>
                              <p className="font-semibold text-gray-800 text-xs">
                                Two-Factor Authentication
                              </p>
                              <p className="text-xs text-gray-600 font-medium">
                                Add extra security to your account
                              </p>
                            </div>
                            <button
                              onClick={() =>
                                handleSettingChange(
                                  "twoFactorAuth",
                                  !userSettings.twoFactorAuth,
                                )
                              }
                              className={`px-3 py-1.5 rounded-lg font-bold text-xs transition-all shadow-sm hover:shadow-md ${userSettings.twoFactorAuth
                                ? "bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700"
                                : "bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700"
                                }`}
                            >
                              {userSettings.twoFactorAuth
                                ? "Enabled"
                                : "Enable 2FA"}
                            </button>
                          </div>

                          <div className="flex items-center justify-between p-2.5 bg-white/60 border border-red-100/60 rounded-lg transition-all hover:shadow-sm">
                            <div>
                              <p className="font-semibold text-gray-800 text-xs">
                                Login Notifications
                              </p>
                              <p className="text-xs text-gray-600 font-medium">
                                Get notified when someone logs into your account
                              </p>
                            </div>
                            <input
                              type="checkbox"
                              checked={userSettings.loginAlerts ?? true}
                              onChange={(e) =>
                                handleSettingChange(
                                  "loginAlerts",
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 text-red-600 rounded focus:ring-2 focus:ring-red-400 cursor-pointer"
                            />
                          </div>

                          <div className="flex items-center justify-between p-2.5 bg-white/60 border border-red-100/60 rounded-lg transition-all hover:shadow-sm">
                            <div>
                              <p className="font-semibold text-gray-800 text-xs">
                                Change Password
                              </p>
                              <p className="text-xs text-gray-600 font-medium">
                                Update your account password
                              </p>
                            </div>
                            <button
                              onClick={() => setShowChangePasswordModal(true)}
                              className="px-3 py-1.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg hover:from-gray-600 hover:to-gray-700 font-bold text-xs transition-all shadow-sm hover:shadow-md"
                            >
                              Change Password
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Language & Region */}
                      <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-4 shadow-md">
                        <h4 className="text-base font-bold text-red-700 mb-3 flex items-center">
                          <Globe className="w-4 h-4 mr-1.5" />
                          Language & Region
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-red-600 mb-1.5">
                              Language
                            </label>
                            <select
                              value={userSettings.preferredLanguage || "EN"}
                              onChange={(e) =>
                                handleSettingChange(
                                  "preferredLanguage",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="EN">English</option>
                              <option value="HI">हिंदी</option>
                              <option value="BN">বাংলা</option>
                              <option value="TA">தமிழ்</option>
                              <option value="TE">తెలుగు</option>
                              <option value="MR">मराठी</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-red-600 mb-1.5">
                              Currency
                            </label>
                            <select
                              value={userSettings.currency || "INR"}
                              onChange={(e) =>
                                handleSettingChange("currency", e.target.value)
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="INR">₹ Indian Rupee (INR)</option>
                              <option value="USD">$ US Dollar (USD)</option>
                              <option value="EUR">€ Euro (EUR)</option>
                              <option value="GBP">£ British Pound (GBP)</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-red-600 mb-1.5">
                              Time Zone
                            </label>
                            <select
                              value={userSettings.timeZone || "Asia/Kolkata"}
                              onChange={(e) =>
                                handleSettingChange("timeZone", e.target.value)
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="Asia/Kolkata">
                                India Standard Time (IST)
                              </option>
                              <option value="America/New_York">
                                Eastern Time (ET)
                              </option>
                              <option value="Europe/London">
                                Greenwich Mean Time (GMT)
                              </option>
                              <option value="Asia/Dubai">
                                Gulf Standard Time (GST)
                              </option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-red-600 mb-1.5">
                              Date Format
                            </label>
                            <select
                              value={userSettings.dateFormat || "DD/MM/YYYY"}
                              onChange={(e) =>
                                handleSettingChange(
                                  "dateFormat",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-red-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80 text-gray-800 font-medium shadow-sm"
                            >
                              <option value="DD/MM/YYYY">
                                DD/MM/YYYY (Indian)
                              </option>
                              <option value="MM/DD/YYYY">
                                MM/DD/YYYY (US)
                              </option>
                              <option value="YYYY-MM-DD">
                                YYYY-MM-DD (ISO)
                              </option>
                              <option value="DD-MMM-YYYY">DD-MMM-YYYY</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Account Management */}
                      <div className="bg-gradient-to-br from-white/90 to-red-50/60 border border-red-200/60 rounded-xl p-4 shadow-md">
                        <h4 className="text-base font-bold text-red-700 mb-3">
                          Account Management
                        </h4>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-2.5 bg-yellow-50 rounded-lg border border-yellow-200 transition-all hover:shadow-sm">
                            <div>
                              <p className="font-semibold text-yellow-800 text-xs">
                                Export My Data as PDF
                              </p>
                              <p className="text-xs text-yellow-600 font-medium">
                                Download a professional PDF report of your
                                account data
                              </p>
                            </div>
                            <button
                              onClick={handleExportData}
                              disabled={isExportingData}
                              className="px-3 py-1.5 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-xs font-bold shadow-sm hover:shadow-md transition-all"
                            >
                              {isExportingData ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                              ) : (
                                <Download className="w-3.5 h-3.5 mr-1.5" />
                              )}
                              {isExportingData
                                ? "Generating PDF..."
                                : "Export as PDF"}
                            </button>
                          </div>

                          <div className="flex items-center justify-between p-2.5 bg-red-50 rounded-lg border border-red-200 transition-all hover:shadow-sm">
                            <div>
                              <p className="font-semibold text-red-800 text-xs">
                                Delete Account
                              </p>
                              <p className="text-xs text-red-600 font-medium">
                                Permanently delete your account and all data
                              </p>
                            </div>
                            <button
                              onClick={() => setShowDeleteAccountModal(true)}
                              className="px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center text-xs font-bold shadow-sm hover:shadow-md transition-all"
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                              Delete Account
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          style={{ padding: window.innerWidth < 640 ? "12px" : "16px" }}
        >
          <div
            className="bg-white rounded-lg max-w-md w-full"
            style={{
              padding: window.innerWidth < 640 ? "20px" : "24px",
              margin: "0 auto",
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Change Password
              </h3>
              <button
                onClick={() => setShowChangePasswordModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-100" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      currentPassword: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Enter current password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      newPassword: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      confirmPassword: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Confirm new password"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowChangePasswordModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordChange}
                  disabled={
                    passwordUpdating ||
                    !passwordData.currentPassword ||
                    !passwordData.newPassword ||
                    !passwordData.confirmPassword
                  }
                  className="flex-1 px-4 py-2 bg-red-100 text-white rounded-md hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center justify-center"
                >
                  {passwordUpdating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Update Password"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteAccountModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          style={{ padding: window.innerWidth < 640 ? "12px" : "16px" }}
        >
          <div
            className="bg-white rounded-lg max-w-md w-full"
            style={{
              padding: window.innerWidth < 640 ? "20px" : "24px",
              margin: "0 auto",
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-red-900">
                Delete Account
              </h3>
              <button
                onClick={() => setShowDeleteAccountModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-100" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-red-800">
                      Warning: This action cannot be undone
                    </h4>
                    <p className="text-sm text-red-700 mt-1">
                      Deleting your account will permanently remove all your
                      data including:
                    </p>
                    <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                      <li>Profile information</li>
                      <li>Booking history</li>
                      <li>Payment methods</li>
                      <li>Subscription details</li>
                      <li>All personal data</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type "DELETE" to confirm account deletion:
                </label>
                <input
                  type="text"
                  placeholder="Type DELETE here"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-100"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowDeleteAccountModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={true}
                  className="flex-1 px-4 py-2 bg-red-100 text-white rounded-md hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center justify-center"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compact Eye-Catching Cancellation Modal */}
      {showCancelModal && cancelBookingData && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "15px",
            backdropFilter: "blur(10px)",
            animation: "fadeIn 0.25s ease-out",
          }}
          onClick={() => setShowCancelModal(false)}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "20px",
              maxWidth: "420px",
              width: "100%",
              boxShadow:
                "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.1)",
              animation:
                "modalBounce 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
              overflow: "hidden",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Compact Gradient Header */}
            <div
              style={{
                background: `linear-gradient(135deg, ${cancelBookingData.refundColor === "green"
                  ? "#10b981 0%, #059669 100%"
                  : cancelBookingData.refundColor === "orange"
                    ? "#f59e0b 0%, #d97706 100%"
                    : "#ef4444 0%, #dc2626 100%"
                  })`,
                padding: window.innerWidth < 640 ? "16px" : "20px",
                color: "white",
                position: "relative",
                overflow: "hidden",
                textAlign: "center",
              }}
            >
              {/* Decorative circles */}
              <div
                style={{
                  position: "absolute",
                  top: "-30px",
                  right: "-30px",
                  width: "100px",
                  height: "100px",
                  background: "rgba(255, 255, 255, 0.15)",
                  borderRadius: "50%",
                  filter: "blur(20px)",
                }}
              ></div>
              <div
                style={{
                  position: "absolute",
                  bottom: "-20px",
                  left: "-20px",
                  width: "80px",
                  height: "80px",
                  background: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "50%",
                  filter: "blur(15px)",
                }}
              ></div>

              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    fontSize: "36px",
                    filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.3))",
                    animation: "pulse 2s ease-in-out infinite",
                  }}
                >
                  {cancelBookingData.refundIcon}
                </div>
                <div style={{ textAlign: "left" }}>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      opacity: 0.95,
                      letterSpacing: "0.5px",
                      marginBottom: "2px",
                    }}
                  >
                    {cancelBookingData.refundType}
                  </div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "900",
                      letterSpacing: "-1px",
                      textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  >
                    {cancelBookingData.refundPercentage}
                  </div>
                </div>
              </div>
            </div>

            {/* Compact Content */}
            <div style={{ padding: "20px" }}>
              {/* Booking Info Grid */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                  borderRadius: "14px",
                  padding: "16px",
                  marginBottom: "14px",
                  border: "2px solid #fbbf24",
                  boxShadow: "0 2px 8px rgba(251, 191, 36, 0.15)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Decorative corner accent */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "60px",
                    height: "60px",
                    background:
                      "linear-gradient(135deg, rgba(245, 158, 11, 0.2), transparent)",
                    borderRadius: "0 0 0 100%",
                  }}
                ></div>

                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "24px",
                        width: "36px",
                        height: "36px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(251, 191, 36, 0.2)",
                        borderRadius: "10px",
                        flexShrink: 0,
                      }}
                    >
                      {cancelBookingData.bookingType === "hotel" ? "🏨" : "🍽️"}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#92400e",
                          fontWeight: "600",
                          marginBottom: "2px",
                        }}
                      >
                        {cancelBookingData.bookingType === "hotel"
                          ? "Hotel"
                          : "Restaurant"}
                      </div>
                      <div
                        style={{
                          fontSize: "15px",
                          color: "#78350f",
                          fontWeight: "700",
                          lineHeight: "1.2",
                        }}
                      >
                        {cancelBookingData.bookingType === "hotel"
                          ? cancelBookingData.booking.hotelName
                          : cancelBookingData.booking.restaurantName}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "10px",
                      paddingTop: "10px",
                      borderTop: "2px dashed rgba(251, 191, 36, 0.3)",
                    }}
                  >
                    {cancelBookingData.bookingType === "hotel" ? (
                      <div>
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#92400e",
                            fontWeight: "600",
                            marginBottom: "3px",
                          }}
                        >
                          📅 Check-in
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#78350f",
                            fontWeight: "700",
                          }}
                        >
                          {new Date(
                            cancelBookingData.booking.checkIn,
                          ).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                          })}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <div
                            style={{
                              fontSize: "10px",
                              color: "#92400e",
                              fontWeight: "600",
                              marginBottom: "3px",
                            }}
                          >
                            📅 Date
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#78350f",
                              fontWeight: "700",
                            }}
                          >
                            {new Date(
                              cancelBookingData.booking.bookingDate,
                            ).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "short",
                            })}
                          </div>
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: "10px",
                              color: "#92400e",
                              fontWeight: "600",
                              marginBottom: "3px",
                            }}
                          >
                            ⏰ Time
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#78350f",
                              fontWeight: "700",
                            }}
                          >
                            {cancelBookingData.booking.bookingTime}
                          </div>
                        </div>
                      </>
                    )}
                    <div
                      style={{
                        gridColumn:
                          cancelBookingData.bookingType === "hotel"
                            ? "span 1"
                            : "span 2",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#92400e",
                          fontWeight: "600",
                          marginBottom: "3px",
                        }}
                      >
                        💰 Amount
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          color: "#78350f",
                          fontWeight: "900",
                        }}
                      >
                        ₹{cancelBookingData.booking.totalAmount || 0}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Compact Info Message */}
              <div
                style={{
                  background:
                    cancelBookingData.refundPercentage !== "0%"
                      ? "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
                      : "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                  borderRadius: "12px",
                  padding: "12px 14px",
                  marginBottom: "14px",
                  border: `1.5px solid ${cancelBookingData.refundPercentage !== "0%"
                    ? "#60a5fa"
                    : "#f87171"
                    }`,
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: "20px", flexShrink: 0 }}>
                  {cancelBookingData.refundPercentage !== "0%" ? "ℹ️" : "⚠️"}
                </span>
                <p
                  style={{
                    margin: 0,
                    color:
                      cancelBookingData.refundPercentage !== "0%"
                        ? "#1e3a8a"
                        : "#991b1b",
                    fontSize: "12px",
                    lineHeight: "1.5",
                    fontWeight: "600",
                  }}
                >
                  {cancelBookingData.refundPercentage !== "0%" ? (
                    <>Refund in 5-7 days to original payment method</>
                  ) : (
                    <>
                      Non-refundable within{" "}
                      {cancelBookingData.bookingType === "hotel" ? "24h" : "2h"}{" "}
                      of booking
                    </>
                  )}
                </p>
              </div>

              {/* Email Badge */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)",
                  borderRadius: "10px",
                  padding: "10px 12px",
                  marginBottom: "16px",
                  border: "1.5px solid #c084fc",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "center",
                }}
              >
                <span style={{ fontSize: "16px" }}>📧</span>
                <span
                  style={{
                    color: "#581c87",
                    fontSize: "11px",
                    fontWeight: "600",
                  }}
                >
                  Confirmation email will be sent
                </span>
              </div>

              {/* Compact Action Buttons */}
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelBookingData(null);
                  }}
                  style={{
                    flex: 1,
                    padding: "13px 18px",
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                    border: "none",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "700",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#e5e7eb";
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#f3f4f6";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.08)";
                  }}
                >
                  ← Keep
                </button>
                <button
                  onClick={confirmCancellation}
                  style={{
                    flex: 1.5,
                    padding: "13px 18px",
                    background:
                      "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "800",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    boxShadow: "0 4px 12px rgba(239, 68, 68, 0.4)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-2px) scale(1.02)";
                    e.target.style.boxShadow =
                      "0 8px 20px rgba(239, 68, 68, 0.5)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0) scale(1)";
                    e.target.style.boxShadow =
                      "0 4px 12px rgba(239, 68, 68, 0.4)";
                  }}
                >
                  ✓ Confirm Cancel
                </button>
              </div>
            </div>
          </div>

          {/* Enhanced CSS animations */}
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes modalBounce {
                0% {
                  opacity: 0;
                  transform: scale(0.7) translateY(-20px);
                }
                50% {
                  transform: scale(1.05) translateY(5px);
                }
                100% {
                  opacity: 1;
                  transform: scale(1) translateY(0);
                }
              }
              @keyframes pulse {
                0%, 100% {
                  transform: scale(1);
                }
                50% {
                  transform: scale(1.1);
                }
              }

              /* Responsive Styles */
              @media (max-width: 768px) {
                .space-y-4 > * + * {
                  margin-top: 0.75rem !important;
                }
                
                .space-y-5 > * + * {
                  margin-top: 1rem !important;
                }

                /* Make cards more compact on mobile */
                .bg-white.rounded-lg, .bg-gradient-to-br {
                  border-radius: 12px !important;
                }

                /* Reduce font sizes slightly on mobile */
                h3, .text-xl {
                  font-size: 1.125rem !important;
                }

                h4, .text-lg {
                  font-size: 1rem !important;
                }

                /* Make buttons stack better */
                .flex.space-x-3 {
                  flex-direction: column !important;
                  gap: 0.75rem !important;
                }

                .flex.space-x-3 > button {
                  width: 100% !important;
                }
              }

              @media (max-width: 640px) {
                /* Extra small screens */
                .p-4, .p-6 {
                  padding: 0.75rem !important;
                }

                .px-6 {
                  padding-left: 1rem !important;
                  padding-right: 1rem !important;
                }

                .py-3 {
                  padding-top: 0.5rem !important;
                  padding-bottom: 0.5rem !important;
                }

                /* Make inputs larger for touch */
                input, select, textarea {
                  min-height: 42px !important;
                  font-size: 16px !important; /* Prevents zoom on iOS */
                }

                button {
                  min-height: 42px !important;
                  font-size: 14px !important;
                }
              }
            `}
          </style>
        </div>
      )}

      {/* Refund Request Modal */}
      <RefundRequestModal
        isOpen={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          setRefundBooking(null);
          setRefundBookingType(null);
        }}
        booking={refundBooking}
        bookingType={refundBookingType}
        onRefundCreated={handleRefundCreated}
      />
    </div>
  );
};

export default ProfilePage;
